// ===== Packages =====

import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
}                                           from 'react';
import moment                               from 'moment-timezone';
import { Editor }                           from 'slate';
import { useNavigate }                      from 'react-router-dom';
import { ErrorBoundary }                    from 'react-error-boundary';

// ===== Components =====

import Modal                                from '../Modal';
import Spinner                              from '../Spinner';
import ToggleButton                         from '../ToggleButton';
import {
    PostEditor,
}                                           from '../Editor/editors';
import {
    Button,
    GenericModal,
}                                           from '../Editor/helpers';
import {
    HistoryAccessActions,
    HistoryAccessCalendarDay,
    HistoryAccessCalendarFooter,
    HistoryAccessCalendarGrid,
    HistoryAccessCalendarHeader,
    HistoryAccessCalendarNavButton,
    HistoryAccessCalendarActionButton,
    HistoryAccessCalendarTitle,
    HistoryAccessCalendarWeekday,
    HistoryAccessCalendarWeekdays,
    HistoryAccessDateField,
    HistoryAccessDatePopover,
    HistoryAccessField,
    HistoryAccessGrid,
    HistoryAccessInfoButton,
    HistoryAccessInfoIcon,
    HistoryAccessInfoText,
    HistoryAccessLabelRow,
    HistoryEmptyState,
    HistoryErrorState,
    HistoryListItem,
    HistoryLoadingState,
    HistoryMetaRow,
    HistoryModalCloseButtonContainer,
    HistoryModalBody,
    HistoryModalContainer,
    HistoryModalHeader,
    HistoryModalSubtitle,
    HistoryModalTitle,
    HistoryAccessDateRow,
    HistoryAccessDateTrigger,
    HistoryAccessLabel,
    HistoryActionRow,
    HistoryPreviewCutoffLabel,
    HistoryPreviewCutoffLine,
    HistoryPreviewCutoffMarker,
    HistoryPreviewContainer,
    HistoryPreviewFooter,
    HistoryPreviewPane,
    HistoryAccessPane,
    HistoryAccessSection,
    HistorySidebar,
    HistoryScopeLabel,
    HistoryScopePathRow,
    HistoryScopeSeparator,
    HistoryScopeRow,
    HistoryScopeSelect,
    HistorySinglePaneBody,
    HistorySidebarControls,
    HistorySidebarList,
    HistoryTag,
    HistoryTargetRow,
    HistoryTargetControls,
    HistoryTargetButtonGroup,
    HistoryTargetSelectGroup,
    HistoryTimestamp,
    HistoryVersionLabel,
    HistoryPublishSummaryActions,
    HistoryPublishSummaryCard,
    HistoryPublishSummaryCountLabel,
    HistoryPublishSummaryCountRow,
    HistoryPublishSummaryCountValue,
    HistoryPublishSummaryCounts,
    HistoryPublishSummaryFailedItem,
    HistoryPublishSummaryFailedList,
    HistoryPublishSummaryNote,
    HistoryPublishSummarySubtitle,
    HistoryPublishSummaryTitle,
}                                           from './styles';

// ===== Interfaces =====

import {
    IAccessMetadataDraft,
    IAnnotationItem,
    IAnnotationRepairSummary,
    IMediaItem,
    IModalMessage,
    IPostCollabDocCheckpointItem,
    IPostCollabDocPublishTargetState,
    IReadPostItem,
    ISerializedScopeAccessMetadata,
    ISnackbarItem,
    IZoomLevelItem,
    IPartialUserItem,
}                                           from '../../interfaces';

// ===== Services =====

import {
    exportPostCollaborativeDocCheckpoint,
    getPostCollaborativePublishTargetState,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    publishPostCollaborativePreviewCutoff,
    publishPostScopeAccessMetadata,
    publishPostCollaborativeDoc,
    recordUserAction,
    resolveScopeAccessMetadata,
    scopeRequiresPreviewCutoffForProductionPublish,
    scopeSupportsPreviewCutoffForProductionPublish,
    subscribeToPostCollabDocCheckpoints,
    serializeAccessMetadataDraft,
    buildAccessMetadataDraft,
    accessMetadataDraftHasUnsavedChanges,
    accessMetadataRequiresPreviewCutoff,
    formatInheritedBooleanLabel,
    formatLockedUntilDateInputValue,
    formatLockedUntilSummary,
    updatePostCollaborativePreviewCutoff,
    updatePostScopeAccessMetadata,
}                                           from '../../services';

// ===== Enums =====

import {
    ACCESS_INFO_KEY,
    ACCESS_TOGGLE_MODE,
    BUTTON_TYPE,
    CURSOR_TARGET,
    EDITOR_CONTEXT_TYPE,
    INTERACTABLE_OBJECT,
    LOCKED_UNTIL_MODE,
    POST_COLLAB_DOC_CHECKPOINT_REASON,
    POST_COLLAB_PUBLISH_MODE,
    POST_PUBLISHING_DIALOG_ACCESS_ACTION,
    POST_PUBLISHING_DIALOG_MODE,
    POST_CONTENT_SCOPE,
    PAGE_ROUTE,
    READER_PARAMS_TYPE,
    TOOLTIP_TYPE,
    USER_ACTION_TYPE,
}                                           from '../../enums';
import { ZOOM_LEVEL }                       from '../Editor/elements/enums';
import CrossIcon                            from '../../images/editor/cross.svg';
import DownloadIcon                         from '../../images/download-arrow.svg';
import PaperplaneIcon                       from '../../images/paper-plane.svg';
import SplitPageIcon                        from '../../images/split-page.svg';
import SaveIcon                             from '../../images/floppy-disk.svg';
import ClockIcon                            from '../../images/clock.svg';
import InfoIcon                             from '../../images/info.svg';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    DEFAULT_EDITOR_COLOR,
    HOVER_TARGET_CLASSNAME,
}                                           from '../../constants/generalConstants';
import resolvePostVersionHistoryScope       from '../../services/resolvePostVersionHistoryScope';

// ===== Styles =====

import { theme as themeObj }                from '../../themes/theme-context';

PostPublishingDialog.defaultProps = {
    docId: undefined,
    currentPostValue: undefined,
    currentSessionId: undefined,
    authUserId: null,
    mode: POST_PUBLISHING_DIALOG_MODE.publishing,
    user: null,
    userIsAdmin: false,
};
interface Props {
    visible: boolean,
    test: boolean,
    mode?: POST_PUBLISHING_DIALOG_MODE,
    docId?: string,
    post: IReadPostItem | null,
    currentPostValue?: string,
    selectedPostValuePath: number[],
    currentZoomLevel: IZoomLevelItem | null,
    currentSessionId?: string,
    authUserId?: string | null,
    user?: IPartialUserItem | null,
    userIsAdmin?: boolean,
    customCursorActive: boolean,
    hasSound: boolean,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onSyncScopeAccessMetadata: (
        docId: string,
        serializedAccessMetadata: ISerializedScopeAccessMetadata,
    ) => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    onClose: () => void,
    onRestore: (value: string) => void,
}

const buildRootDocId = (postId: string): string => `${postId}::root`;
const buildChapterDocId = (postId: string, chapterId: string): string => `${postId}::chapter:${chapterId}`;
const buildSectionDocId = (postId: string, sectionId: string): string => `${postId}::section:${sectionId}`;

const resolveScopeFromDocId = (nextDocId?: string): {
    scope: POST_CONTENT_SCOPE,
    scopeId: string | null,
} => {
    if (!nextDocId) {
        return {
            scope: POST_CONTENT_SCOPE.root,
            scopeId: null,
        };
    }

    const rootMatch = nextDocId.match(/^(.*)::root$/);
    if (rootMatch?.[1]) {
        return {
            scope: POST_CONTENT_SCOPE.root,
            scopeId: rootMatch[1],
        };
    }

    const scopedMatch = nextDocId.match(/^(.*)::(chapter|section):(.*)$/);
    if (
        !scopedMatch?.[2]
        || !scopedMatch?.[3]
    ) {
        return {
            scope: POST_CONTENT_SCOPE.root,
            scopeId: null,
        };
    }

    return {
        scope: scopedMatch[2] === POST_CONTENT_SCOPE.section
            ? POST_CONTENT_SCOPE.section
            : POST_CONTENT_SCOPE.chapter,
        scopeId: scopedMatch[3],
    };
};

const formatAnnotationRepairSummarySnackbarText = (
    summary: IAnnotationRepairSummary,
): string => `Full version published. ${summary.reattached} reattached, ${summary.detached} detached, ${summary.unchanged} unchanged, ${summary.failed} failed.`;

const resolvePublishFailureSnackbarText = (data: {
    publishMode: POST_COLLAB_PUBLISH_MODE,
    error: unknown,
}): string => {
    const {
        publishMode,
        error,
    } = data;
    const defaultText = publishMode === POST_COLLAB_PUBLISH_MODE.preview
        ? 'There was a problem publishing the preview.'
        : 'There was a problem publishing the full version.';

    if (error == null || typeof error !== 'object') {
        return defaultText;
    }

    const errorCode = 'code' in error && typeof error.code === 'string'
        ? error.code
        : '';
    if (
        errorCode === 'functions/deadline-exceeded'
        || errorCode === 'deadline-exceeded'
    ) {
        return publishMode === POST_COLLAB_PUBLISH_MODE.preview
            ? 'Publishing the preview timed out. Please try again in a moment.'
            : 'Publishing the full version timed out. Please try again in a moment.';
    }

    return defaultText;
};

/**
 * Format one skimmable error string for version-history load and preview
 * failures so console output and user-action telemetry carry the exact scope
 * and checkpoint context that triggered the problem.
 */
const formatVersionHistoryDebugError = (data: {
    message: string,
    error: unknown,
    docId?: string,
    checkpointId: string | null,
    scope: POST_CONTENT_SCOPE,
    selectedPostValuePath: number[],
    zoomLevel?: number,
    authUserId?: string | null,
    appUserId?: string | null,
}): string => {
    const {
        message,
        error,
        docId,
        checkpointId,
        scope,
        selectedPostValuePath,
        zoomLevel,
        authUserId,
        appUserId,
    } = data;
    const errorMessage = error instanceof Error
        ? error.message
        : String(error);

    return [
        message,
        `docId=${docId || 'unknown'}`,
        `checkpointId=${checkpointId || 'unknown'}`,
        `scope=${scope}`,
        `path=${selectedPostValuePath.join('.') || 'root'}`,
        `zoom=${zoomLevel != null ? zoomLevel : 'unknown'}`,
        `authUserId=${authUserId || 'unknown'}`,
        `appUserId=${appUserId || 'unknown'}`,
        `error=${errorMessage}`,
    ].join(' | ');
};

const renderVersionHistoryPreviewFallback = (): JSX.Element => (
    <HistoryEmptyState>
        There was a problem rendering that version preview.
    </HistoryEmptyState>
);

const buildDetachedAnnotationsRoute = (data: {
    chapterId?: string | null,
    sectionId?: string | null,
}): string => {
    const {
        chapterId,
        sectionId,
    } = data;
    const searchParams = new URLSearchParams([
        [READER_PARAMS_TYPE.zoom, '5'],
    ]);

    if (chapterId) {
        searchParams.set(READER_PARAMS_TYPE.chapterId, chapterId);
    }
    if (sectionId) {
        searchParams.set(READER_PARAMS_TYPE.sectionId, sectionId);
    }

    return `/${PAGE_ROUTE.book}?${searchParams.toString()}`;
};

const EMPTY_ANNOTATIONS_MAP_SETTER = (() => undefined) as React.Dispatch<React.SetStateAction<Map<string, IAnnotationItem>>>;
const EMPTY_EDITOR_SETTER = (() => undefined) as (editor: Editor | null) => void;
const EMPTY_BOOLEAN_SETTER = (() => undefined) as React.Dispatch<React.SetStateAction<boolean>>;
const EMPTY_INPUT_FOCUS_SETTER = (() => undefined) as React.Dispatch<React.SetStateAction<boolean>>;
const EMPTY_MEDIA_MAP = new Map<string, IMediaItem>();
const EMPTY_FILES: File[] = [];

function PostPublishingDialog({
    visible,
    test,
    mode,
    docId,
    post,
    currentPostValue,
    selectedPostValuePath,
    currentZoomLevel,
    currentSessionId,
    authUserId,
    user,
    userIsAdmin = false,
    customCursorActive,
    hasSound,
    setSnackbarData,
    onSyncScopeAccessMetadata,
    onCursorEnter,
    onCursorLeave,
    onClose,
    onRestore,
}: Props): JSX.Element | null {
    // ===== Constants =====

    const VERSION_HISTORY_RESTORE_MODAL_TITLE = 'Restore this version?';
    const VERSION_HISTORY_RESTORE_MODAL_MESSAGE = (
        'This will replace the current draft with the selected historical version '
        + 'and create a new latest checkpoint.'
    );
    const VERSION_HISTORY_PUBLISH_FULL_MODAL_TITLE = 'Publish full version to production?';
    const VERSION_HISTORY_PUBLISH_FULL_MODAL_MESSAGE = (
        'This will publish the selected checkpoint to the production collaborative doc '
        + 'for this scope.'
    );
    const VERSION_HISTORY_PUBLISH_PREVIEW_MODAL_TITLE = 'Publish preview to production?';
    const VERSION_HISTORY_PUBLISH_PREVIEW_MODAL_MESSAGE = (
        'This will copy the selected checkpoint into production as a preview source for '
        + 'this scope and use the saved preview cutoff to generate preview responses '
        + 'without changing the production full-content snapshot.'
    );
    const VERSION_HISTORY_PREVIEW_CUTOFF_TOOLTIP = 'Set and save a preview cutoff on this version before publishing this scope to production.';
    const VERSION_HISTORY_PREVIEW_SCOPE_TOOLTIP_ROOT = 'Preview cutoffs and preview publishing are only available on leaf scopes. Select a chapter or section instead of the post scope.';
    const VERSION_HISTORY_PREVIEW_SCOPE_TOOLTIP_CHAPTER = 'Preview cutoffs and preview publishing are only available on leaf scopes. Select a section instead of the chapter scope.';
    const VERSION_HISTORY_PREVIEW_CUTOFF_ONLY_TOOLTIP = 'Publish this scope to production before publishing only the cutoff.';
    const VERSION_HISTORY_PREVIEW_PUBLISH_TARGET_TOOLTIP = 'Publish full production content for this scope before publishing a preview.';
    const VERSION_HISTORY_PREVIEW_CUTOFF_ALREADY_PUBLISHED_TOOLTIP = 'This preview cutoff is already published to production.';
    const VERSION_HISTORY_PREVIEW_ALREADY_PUBLISHED_TOOLTIP = 'This checkpoint and cutoff are already published as the current production preview.';
    const VERSION_HISTORY_FULL_ALREADY_PUBLISHED_TOOLTIP = 'This checkpoint is already published as the current production full version.';
    const VERSION_HISTORY_SET_CUTOFF_TOOLTIP = 'Turn on cutoff selection, then click a paragraph in the preview to choose where the production preview ends for this checkpoint.';
    const VERSION_HISTORY_ACCESS_SAVE_TOOLTIP = 'No access changes to save.';
    const VERSION_HISTORY_ACCESS_SAVE_AND_PUBLISH_TOOLTIP = 'No access changes to save and publish.';
    const VERSION_HISTORY_ACCESS_PUBLISH_UNSAVED_TOOLTIP = 'Save access changes before publishing them to production.';
    const VERSION_HISTORY_ACCESS_PUBLISH_NOOP_TOOLTIP = 'Production access metadata already matches staging for this scope.';
    const VERSION_HISTORY_ACCESS_PUBLISH_SUCCESS_WITH_SAVE = 'Access settings saved to staging and published to production!';
    const VERSION_HISTORY_ACCESS_PUBLISH_SAVE_ONLY_SUCCESS = 'Access settings saved to staging. Production already matches this scope.';
    const VERSION_HISTORY_ACCESS_PUBLISH_AFTER_SAVE_FAILURE = 'Access settings were saved to staging, but there was a problem publishing them to production.';
    const VERSION_HISTORY_EXPORT_JSON_TOOLTIP = 'Export the selected scope checkpoint JSON only. Aggregate scopes do not bundle nested chapter or section checkpoint JSON.';
    const isPublishingMode = mode === POST_PUBLISHING_DIALOG_MODE.publishing;
    const isAccessMode = mode === POST_PUBLISHING_DIALOG_MODE.access;

    // ===== React Router =====

    const navigate = useNavigate();

    // ===== Refs =====

    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const previewCutoffMarkerMeasureFrameRef = useRef<number | null>(null);
    const accessCalendarRef = useRef<HTMLDivElement | null>(null);
    const hasSeededHistoryScopeRef = useRef<boolean>(false);
    const lastScopeSeedKeyRef = useRef<string | null>(null);

    // ===== State =====

    const [selectedDialogScope, setSelectedDialogScope] = useState<POST_CONTENT_SCOPE>(POST_CONTENT_SCOPE.root);
    const [selectedDialogChapterId, setSelectedDialogChapterId] = useState<string | null>(null);
    const [selectedDialogSectionId, setSelectedDialogSectionId] = useState<string | null>(null);
    const [selectedHistoryScope, setSelectedHistoryScope] = useState<POST_CONTENT_SCOPE>(POST_CONTENT_SCOPE.root);
    const [selectedHistoryChapterId, setSelectedHistoryChapterId] = useState<string | null>(null);
    const [selectedHistorySectionId, setSelectedHistorySectionId] = useState<string | null>(null);
    const [checkpoints, setCheckpoints] = useState<IPostCollabDocCheckpointItem[]>([]);
    const [loadingCheckpoints, setLoadingCheckpoints] = useState<boolean>(false);
    const [checkpointLoadFailed, setCheckpointLoadFailed] = useState<boolean>(false);
    const [selectedCheckpointId, setSelectedCheckpointId] = useState<string | null>(null);
    const [selectedCheckpointValue, setSelectedCheckpointValue] = useState<string>('');
    const [loadingSelectedCheckpoint, setLoadingSelectedCheckpoint] = useState<boolean>(false);
    const [definePreviewMode, setDefinePreviewMode] = useState<boolean>(false);
    const [savingPreviewCutoff, setSavingPreviewCutoff] = useState<boolean>(false);
    const [selectedPreviewCutoffParagraphId, setSelectedPreviewCutoffParagraphId] = useState<string | null>(null);
    const [selectedPreviewCutoffCheckpointId, setSelectedPreviewCutoffCheckpointId] = useState<string | null>(null);
    const [previewCutoffMarkerTop, setPreviewCutoffMarkerTop] = useState<number | null>(null);
    const [publishingMode, setPublishingMode] = useState<POST_COLLAB_PUBLISH_MODE | null>(null);
    const [publishModalMode, setPublishModalMode] = useState<POST_COLLAB_PUBLISH_MODE | null>(null);
    const [annotationRepairSummary, setAnnotationRepairSummary] = useState<IAnnotationRepairSummary | null>(null);
    const [annotationRepairSummaryDismissed, setAnnotationRepairSummaryDismissed] = useState<boolean>(false);
    const [annotationRepairFailedDetailsVisible, setAnnotationRepairFailedDetailsVisible] = useState<boolean>(false);
    const [detachedAnnotationsRoute, setDetachedAnnotationsRoute] = useState<string | null>(null);
    const [publishTargetState, setPublishTargetState] = useState<IPostCollabDocPublishTargetState | null>(null);
    const [savedScopeAccessMetadata, setSavedScopeAccessMetadata] = useState<ReturnType<typeof resolveScopeAccessMetadata>>(null);
    const [savingAccessMetadata, setSavingAccessMetadata] = useState<boolean>(false);
    const [publishingAccessMetadata, setPublishingAccessMetadata] = useState<boolean>(false);
    const [activeAccessAction, setActiveAccessAction] = useState<POST_PUBLISHING_DIALOG_ACCESS_ACTION | null>(null);
    const [accessPaywalledMode, setAccessPaywalledMode] = useState<ACCESS_TOGGLE_MODE>(ACCESS_TOGGLE_MODE.off);
    const [accessAuthLockedMode, setAccessAuthLockedMode] = useState<ACCESS_TOGGLE_MODE>(ACCESS_TOGGLE_MODE.off);
    const [accessLockedUntilMode, setAccessLockedUntilMode] = useState<LOCKED_UNTIL_MODE>(LOCKED_UNTIL_MODE.date);
    const [accessLockedUntilDate, setAccessLockedUntilDate] = useState<string>('');
    const [accessCalendarVisible, setAccessCalendarVisible] = useState<boolean>(false);
    const [accessCalendarMonth, setAccessCalendarMonth] = useState<string>('');
    const [restoreModalVisible, setRestoreModalVisible] = useState<boolean>(false);
    const [activeAccessInfoKeys, setActiveAccessInfoKeys] = useState<Record<ACCESS_INFO_KEY, boolean>>({
        [ACCESS_INFO_KEY.paywall]: false,
        [ACCESS_INFO_KEY.authLock]: false,
        [ACCESS_INFO_KEY.lockedUntil]: false,
    });

    const markSelectedCheckpointPublishedLocally = useCallback((
        publishMode: POST_COLLAB_PUBLISH_MODE,
        publishedAt: number,
    ) => {
        if (!selectedCheckpointId) return;

        setCheckpoints((currentCheckpoints) => currentCheckpoints.map((checkpoint) => (
            checkpoint.id === selectedCheckpointId
                ? {
                    ...checkpoint,
                    publishedToProductionAt: publishedAt,
                    publishedToProductionMode: publishMode,
                }
                : checkpoint
        )));
    }, [
        selectedCheckpointId,
    ]);

    const postChapters = useMemo(() => (
        post?.chapters || []
    ), [
        post,
    ]);
    const selectedDialogChapter = useMemo(() => (
        postChapters.find((chapter) => chapter.id === selectedDialogChapterId) || null
    ), [
        postChapters,
        selectedDialogChapterId,
    ]);
    const selectedDialogChapterSections = useMemo(() => (
        selectedDialogChapter?.sections || []
    ), [
        selectedDialogChapter,
    ]);
    const selectedDialogSection = useMemo(() => (
        selectedDialogChapterSections.find((section) => section.id === selectedDialogSectionId) || null
    ), [
        selectedDialogChapterSections,
        selectedDialogSectionId,
    ]);
    const resolvedHistoryScope = useMemo(() => (
        resolvePostVersionHistoryScope(post, {
            scope: selectedHistoryScope,
            chapterId: selectedHistoryChapterId,
            sectionId: selectedHistorySectionId,
        })
    ), [
        post,
        selectedHistoryChapterId,
        selectedHistoryScope,
        selectedHistorySectionId,
    ]);
    const historyRootAvailable = resolvedHistoryScope.rootAvailable;
    const historyChapterOptions = resolvedHistoryScope.chapterOptions;
    const historySectionChapterOptions = resolvedHistoryScope.sectionChapterOptions;
    const resolvedSelectedHistoryScope = resolvedHistoryScope.selection.scope;
    const resolvedSelectedHistoryChapterId = resolvedHistoryScope.selection.chapterId;
    const resolvedSelectedHistorySectionId = resolvedHistoryScope.selection.sectionId;
    const selectedHistoryChapter = useMemo(() => (
        postChapters.find((chapter) => chapter.id === resolvedSelectedHistoryChapterId) || null
    ), [
        postChapters,
        resolvedSelectedHistoryChapterId,
    ]);
    const selectedHistoryChapterSections = useMemo(() => (
        selectedHistoryChapter?.sections || []
    ), [
        selectedHistoryChapter,
    ]);
    const canShowHistorySectionButton = useMemo(() => {
        if (historySectionChapterOptions.length === 0) {
            return false;
        }
        if (resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.chapter) {
            return selectedHistoryChapterSections.length > 0;
        }
        return true;
    }, [
        historySectionChapterOptions.length,
        resolvedSelectedHistoryScope,
        selectedHistoryChapterSections.length,
    ]);
    const canShowTargetSectionButton = useMemo(() => {
        if (postChapters.length === 0) {
            return false;
        }
        if (selectedDialogScope === POST_CONTENT_SCOPE.chapter) {
            return selectedDialogChapterSections.length > 0;
        }
        return selectedDialogChapterSections.length > 0
            || postChapters.some((chapter) => (chapter.sections || []).length > 0);
    }, [
        postChapters,
        selectedDialogChapterSections.length,
        selectedDialogScope,
    ]);
    const scopeSeedKey = useMemo(() => {
        if (
            !visible
            || !post
        ) {
            return null;
        }

        const chapterStructureKey = postChapters.map((chapter) => (
            `${chapter.id}:${(chapter.sections || []).map((section) => section.id).join(',')}`
        )).join('|');

        return [
            docId || '',
            post.id,
            selectedPostValuePath.join('.'),
            chapterStructureKey,
        ].join('::');
    }, [
        docId,
        post,
        postChapters,
        selectedPostValuePath,
        visible,
    ]);

    useEffect(() => {
        if (!visible) {
            lastScopeSeedKeyRef.current = null;
            return;
        }
        if (
            !post
            || !scopeSeedKey
        ) {
            return;
        }
        if (lastScopeSeedKeyRef.current === scopeSeedKey) {
            return;
        }
        lastScopeSeedKeyRef.current = scopeSeedKey;

        const resolvedScope = resolveScopeFromDocId(docId);
        let currentChapterId: string | null = null;
        let currentSectionId: string | null = null;

        if (resolvedScope.scope === POST_CONTENT_SCOPE.chapter) {
            currentChapterId = resolvedScope.scopeId;
        } else if (resolvedScope.scope === POST_CONTENT_SCOPE.section) {
            currentSectionId = resolvedScope.scopeId
                || postChapters[selectedPostValuePath[0] || 0]?.sections?.[selectedPostValuePath[1] || 0]?.id
                || null;
            currentChapterId = postChapters.find((chapter) => (
                (chapter.sections || []).some((section) => section.id === currentSectionId)
            ))?.id || postChapters[selectedPostValuePath[0] || 0]?.id || null;
        } else {
            currentChapterId = postChapters[selectedPostValuePath[0] || 0]?.id || null;
            if (
                currentChapterId
                && selectedPostValuePath.length > 1
            ) {
                currentSectionId = postChapters.find((chapter) => chapter.id === currentChapterId)?.sections?.[selectedPostValuePath[1]]
                    ?.id || null;
            }
        }

        const resolvedHistorySelection = resolvePostVersionHistoryScope(post, {
            scope: resolvedScope.scope,
            chapterId: currentChapterId || postChapters[0]?.id || null,
            sectionId: currentSectionId,
        }).selection;

        setSelectedDialogScope(resolvedScope.scope);
        setSelectedDialogChapterId(currentChapterId || postChapters[0]?.id || null);
        setSelectedDialogSectionId(currentSectionId);
        setSelectedHistoryScope(resolvedHistorySelection.scope);
        setSelectedHistoryChapterId(resolvedHistorySelection.chapterId);
        setSelectedHistorySectionId(resolvedHistorySelection.sectionId);
        hasSeededHistoryScopeRef.current = true;
    }, [
        docId,
        post,
        postChapters,
        selectedPostValuePath,
        scopeSeedKey,
        visible,
    ]);

    useEffect(() => {
        if (
            selectedDialogScope === POST_CONTENT_SCOPE.section
            && !selectedDialogSection
        ) {
            if (selectedDialogChapterSections[0]?.id) {
                setSelectedDialogSectionId(selectedDialogChapterSections[0].id);
            } else {
                setSelectedDialogScope(POST_CONTENT_SCOPE.chapter);
                setSelectedDialogSectionId(null);
            }
            return;
        }

        if (
            selectedDialogScope === POST_CONTENT_SCOPE.chapter
            && !selectedDialogChapter
            && postChapters[0]?.id
        ) {
            setSelectedDialogChapterId(postChapters[0].id);
        }
    }, [
        postChapters,
        selectedDialogChapter,
        selectedDialogChapterSections,
        selectedDialogScope,
        selectedDialogSection,
    ]);
    const selectedDialogDocId = useMemo(() => {
        if (!post?.id) return docId;

        if (
            selectedDialogScope === POST_CONTENT_SCOPE.section
            && selectedDialogSectionId
        ) {
            return buildSectionDocId(post.id, selectedDialogSectionId);
        }
        if (
            selectedDialogScope === POST_CONTENT_SCOPE.chapter
            && selectedDialogChapterId
        ) {
            return buildChapterDocId(post.id, selectedDialogChapterId);
        }
        return buildRootDocId(post.id);
    }, [
        docId,
        post,
        selectedDialogChapterId,
        selectedDialogScope,
        selectedDialogSectionId,
    ]);

    const selectedHistoryDocId = useMemo(() => {
        if (!post?.id) return docId;

        if (
            resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section
            && resolvedSelectedHistorySectionId
        ) {
            return buildSectionDocId(post.id, resolvedSelectedHistorySectionId);
        }
        if (
            resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.chapter
            && resolvedSelectedHistoryChapterId
        ) {
            return buildChapterDocId(post.id, resolvedSelectedHistoryChapterId);
        }
        return buildRootDocId(post.id);
    }, [
        docId,
        post,
        resolvedSelectedHistoryChapterId,
        resolvedSelectedHistoryScope,
        resolvedSelectedHistorySectionId,
    ]);
    const selectedHistoryPostValuePath = useMemo(() => {
        if (resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.root) return [];

        const chapterIndex = postChapters.findIndex((chapter) => chapter.id === resolvedSelectedHistoryChapterId);
        if (chapterIndex < 0) return [];
        if (resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.chapter) return [chapterIndex];

        const sectionIndex = (postChapters[chapterIndex]?.sections || []).findIndex((section) => section.id === resolvedSelectedHistorySectionId);
        return sectionIndex >= 0 ? [chapterIndex, sectionIndex] : [chapterIndex];
    }, [
        postChapters,
        resolvedSelectedHistoryChapterId,
        resolvedSelectedHistoryScope,
        resolvedSelectedHistorySectionId,
    ]);
    const historySelectionSettled = (
        selectedHistoryScope === resolvedSelectedHistoryScope
        && selectedHistoryChapterId === resolvedSelectedHistoryChapterId
        && selectedHistorySectionId === resolvedSelectedHistorySectionId
    );
    const selectedHistoryPathKey = useMemo(() => (
        selectedHistoryPostValuePath.join('.') || 'root'
    ), [
        selectedHistoryPostValuePath,
    ]);
    const logVersionHistoryError = useCallback((data: {
        message: string,
        error: unknown,
        checkpointId: string | null,
    }): void => {
        const formattedError = formatVersionHistoryDebugError({
            message: data.message,
            error: data.error,
            docId: selectedHistoryDocId,
            checkpointId: data.checkpointId,
            scope: resolvedSelectedHistoryScope,
            selectedPostValuePath: selectedHistoryPostValuePath,
            zoomLevel: currentZoomLevel?.level,
            authUserId: authUserId || null,
            appUserId: user?.id || null,
        });

        console.error(formattedError, data.error);

        if (!currentSessionId) {
            return;
        }

        recordUserAction({
            test,
            sessionId: currentSessionId,
            type: USER_ACTION_TYPE.error,
            payload: {
                error: formattedError,
            },
        }).catch(() => null);
    }, [
        currentSessionId,
        currentZoomLevel?.level,
        resolvedSelectedHistoryScope,
        selectedHistoryDocId,
        selectedHistoryPostValuePath,
        authUserId,
        test,
        user?.id,
    ]);

    // ===== Side Effects =====

    useEffect(() => {
        if (
            !visible
            || !selectedHistoryDocId
            || !hasSeededHistoryScopeRef.current
            || !historySelectionSettled
        ) {
            setCheckpoints([]);
            setLoadingCheckpoints(false);
            setCheckpointLoadFailed(false);
            setSelectedCheckpointId(null);
            setSelectedCheckpointValue('');
            setDefinePreviewMode(false);
            setSelectedPreviewCutoffParagraphId(null);
            setSelectedPreviewCutoffCheckpointId(null);
            setPreviewCutoffMarkerTop(null);
            return undefined;
        }

        setCheckpoints([]);
        setLoadingCheckpoints(true);
        setCheckpointLoadFailed(false);
        setSelectedCheckpointId(null);
        setSelectedCheckpointValue('');
        setDefinePreviewMode(false);
        setSelectedPreviewCutoffParagraphId(null);
        setSelectedPreviewCutoffCheckpointId(null);
        setPreviewCutoffMarkerTop(null);

        return subscribeToPostCollabDocCheckpoints({
            test,
            docId: selectedHistoryDocId,
            onNext: (nextCheckpoints) => {
                const sortedCheckpoints = [...nextCheckpoints].sort((a, b) => b.version - a.version);
                setLoadingCheckpoints(false);
                setCheckpointLoadFailed(false);
                setCheckpoints(sortedCheckpoints);
                setSelectedCheckpointId((currentSelectedCheckpointId) => {
                    if (
                        currentSelectedCheckpointId
                        && sortedCheckpoints.some((checkpoint) => checkpoint.id === currentSelectedCheckpointId)
                    ) {
                        return currentSelectedCheckpointId;
                    }
                    return sortedCheckpoints[0]?.id || null;
                });
            },
            onError: (error) => {
                setLoadingCheckpoints(false);
                setCheckpointLoadFailed(true);
                logVersionHistoryError({
                    message: 'Version history checkpoint subscription failed.',
                    error,
                    checkpointId: null,
                });
            },
        });
    }, [
        historySelectionSettled,
        logVersionHistoryError,
        visible,
        test,
        selectedHistoryDocId,
    ]);

    useEffect(() => {
        if (
            !visible
            || !selectedHistoryDocId
            || !selectedCheckpointId
            || !hasSeededHistoryScopeRef.current
            || !historySelectionSettled
        ) {
            return;
        }

        let active = true;
        setDefinePreviewMode(false);
        setLoadingSelectedCheckpoint(true);
        exportPostCollaborativeDocCheckpoint({
            test,
            docId: selectedHistoryDocId,
            checkpointId: selectedCheckpointId,
        }).then((result) => {
            if (!active) return;
            setSelectedCheckpointValue(result.value);
            if (
                result.doc.previewCutoffParagraphId
                && result.doc.previewCutoffCheckpointId === selectedCheckpointId
            ) {
                setSelectedPreviewCutoffParagraphId(result.doc.previewCutoffParagraphId);
                setSelectedPreviewCutoffCheckpointId(result.doc.previewCutoffCheckpointId);
            } else {
                setSelectedPreviewCutoffParagraphId(null);
                setSelectedPreviewCutoffCheckpointId(null);
            }
        }).catch((error) => {
            if (!active) return;
            setSelectedCheckpointValue('');
            setSelectedPreviewCutoffParagraphId(null);
            setSelectedPreviewCutoffCheckpointId(null);
            logVersionHistoryError({
                message: 'Failed to export version history checkpoint.',
                error,
                checkpointId: selectedCheckpointId,
            });
            setSnackbarData({
                visible: true,
                text: 'There was a problem loading that version.',
                hasFailure: true,
            });
        }).finally(() => {
            if (active) setLoadingSelectedCheckpoint(false);
        });

        return () => {
            active = false;
        };
    }, [
        historySelectionSettled,
        visible,
        test,
        selectedHistoryDocId,
        selectedCheckpointId,
        logVersionHistoryError,
        setSnackbarData,
    ]);

    useEffect(() => {
        if (visible) return;

        setAnnotationRepairSummary(null);
        setAnnotationRepairSummaryDismissed(false);
        setAnnotationRepairFailedDetailsVisible(false);
        setDetachedAnnotationsRoute(null);
    }, [
        visible,
    ]);
    const fetchPublishTargetState = useCallback(async (
        targetDocId: string,
    ): Promise<IPostCollabDocPublishTargetState | null> => {
        try {
            return await getPostCollaborativePublishTargetState({
                test,
                docId: targetDocId,
            });
        } catch {
            return null;
        }
    }, [
        test,
    ]);

    useEffect(() => {
        if (
            !visible
            || !selectedHistoryDocId
            || !hasSeededHistoryScopeRef.current
            || !historySelectionSettled
        ) {
            setPublishTargetState(null);
            return undefined;
        }

        let active = true;
        setPublishTargetState(null);
        fetchPublishTargetState(selectedHistoryDocId).then((nextState) => {
            if (!active) return;
            setPublishTargetState(nextState);
        });

        return () => {
            active = false;
        };
    }, [
        fetchPublishTargetState,
        historySelectionSettled,
        visible,
        selectedHistoryDocId,
    ]);

    const [targetScopePublishTargetState, setTargetScopePublishTargetState] = useState<IPostCollabDocPublishTargetState | null>(null);

    useEffect(() => {
        if (!visible || !selectedDialogDocId) {
            setTargetScopePublishTargetState(null);
            return undefined;
        }

        let active = true;
        setTargetScopePublishTargetState(null);
        fetchPublishTargetState(selectedDialogDocId).then((nextState) => {
            if (!active) return;
            setTargetScopePublishTargetState(nextState);
        });

        return () => {
            active = false;
        };
    }, [
        fetchPublishTargetState,
        visible,
        selectedDialogDocId,
    ]);

    const selectedCheckpoint = useMemo(() => (
        checkpoints.find((checkpoint) => checkpoint.id === selectedCheckpointId) || null
    ), [
        checkpoints,
        selectedCheckpointId,
    ]);

    const selectedCheckpointIsLatest = useMemo(() => (
        checkpoints[0]?.id === selectedCheckpointId
    ), [
        checkpoints,
        selectedCheckpointId,
    ]);

    const selectedCheckpointHasPreviewCutoff = useMemo(() => (
        !!selectedCheckpointId
        && !!selectedPreviewCutoffParagraphId
        && selectedPreviewCutoffCheckpointId === selectedCheckpointId
    ), [
        selectedCheckpointId,
        selectedPreviewCutoffParagraphId,
        selectedPreviewCutoffCheckpointId,
    ]);

    const currentPublishScope = useMemo((): POST_CONTENT_SCOPE => {
        if (!selectedHistoryDocId) return POST_CONTENT_SCOPE.root;
        if (selectedHistoryDocId.includes(`::${POST_CONTENT_SCOPE.section}:`)) return POST_CONTENT_SCOPE.section;
        if (selectedHistoryDocId.includes(`::${POST_CONTENT_SCOPE.chapter}:`)) return POST_CONTENT_SCOPE.chapter;
        return POST_CONTENT_SCOPE.root;
    }, [
        selectedHistoryDocId,
    ]);
    const resolvedScopeAccessMetadata = useMemo(() => (
        resolveScopeAccessMetadata(post, selectedDialogDocId)
    ), [
        selectedDialogDocId,
        post,
    ]);
    const currentHistoryScopeAccessMetadata = useMemo(() => (
        resolveScopeAccessMetadata(post, selectedHistoryDocId)
    ), [
        post,
        selectedHistoryDocId,
    ]);
    useEffect(() => {
        setSavedScopeAccessMetadata(resolvedScopeAccessMetadata);
    }, [
        resolvedScopeAccessMetadata,
    ]);
    const currentScopeAccessMetadata = savedScopeAccessMetadata;
    const savedAccessDraft = useMemo(() => (
        currentScopeAccessMetadata
            ? buildAccessMetadataDraft(currentScopeAccessMetadata)
            : null
    ), [
        currentScopeAccessMetadata,
    ]);

    useEffect(() => {
        if (!savedAccessDraft) return;
        setAccessPaywalledMode(savedAccessDraft.paywalledMode);
        setAccessAuthLockedMode(savedAccessDraft.authLockedMode);
        setAccessLockedUntilMode(savedAccessDraft.lockedUntilMode);
        setAccessLockedUntilDate(savedAccessDraft.lockedUntilDate);
    }, [
        savedAccessDraft,
    ]);
    useEffect(() => {
        if (
            !visible
            || accessLockedUntilMode !== LOCKED_UNTIL_MODE.date
        ) {
            setAccessCalendarVisible(false);
        }
    }, [
        accessLockedUntilMode,
        currentScopeAccessMetadata,
        visible,
    ]);
    useEffect(() => {
        if (!accessCalendarVisible) {
            return undefined;
        }

        const handlePointerDown = (e: MouseEvent): void => {
            if (
                accessCalendarRef.current
                && !accessCalendarRef.current.contains(e.target as Node)
            ) {
                setAccessCalendarVisible(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [
        accessCalendarVisible,
    ]);

    const currentAccessDraft = useMemo<IAccessMetadataDraft>(() => ({
        paywalledMode: accessPaywalledMode,
        authLockedMode: accessAuthLockedMode,
        lockedUntilMode: accessLockedUntilMode,
        lockedUntilDate: accessLockedUntilDate,
    }), [
        accessAuthLockedMode,
        accessLockedUntilDate,
        accessLockedUntilMode,
        accessPaywalledMode,
    ]);
    const accessHasUnsavedChanges = useMemo(() => (
        currentScopeAccessMetadata
            ? accessMetadataDraftHasUnsavedChanges(currentScopeAccessMetadata, currentAccessDraft)
            : false
    ), [
        currentAccessDraft,
        currentScopeAccessMetadata,
    ]);
    const serializedAccessDraft = useMemo(() => (
        currentScopeAccessMetadata
            ? serializeAccessMetadataDraft(currentScopeAccessMetadata, currentAccessDraft)
            : null
    ), [
        currentAccessDraft,
        currentScopeAccessMetadata,
    ]);
    const accessTargetStateLoaded = !!selectedDialogDocId
        && targetScopePublishTargetState?.sourceDocId === selectedDialogDocId;
    const effectiveDraftAccessState = useMemo(() => {
        if (
            !currentScopeAccessMetadata
            || !serializedAccessDraft
        ) {
            return null;
        }

        return {
            paywalled: serializedAccessDraft.paywalled == null
                ? currentScopeAccessMetadata.inheritedPaywalled
                : serializedAccessDraft.paywalled,
            authLocked: serializedAccessDraft.authLocked == null
                ? currentScopeAccessMetadata.inheritedAuthLocked
                : serializedAccessDraft.authLocked,
        };
    }, [
        currentScopeAccessMetadata,
        serializedAccessDraft,
    ]);
    const normalizedTargetAccessState = useMemo(() => {
        if (
            !currentScopeAccessMetadata
            || !targetScopePublishTargetState
        ) {
            return null;
        }

        let normalizedTargetPaywalled: boolean | null;
        if (currentScopeAccessMetadata.supportsInheritance) {
            if (targetScopePublishTargetState.targetHasPaywalledField) {
                normalizedTargetPaywalled = targetScopePublishTargetState.targetPaywalled ?? false;
            } else {
                normalizedTargetPaywalled = null;
            }
        } else if (typeof targetScopePublishTargetState.targetPaywalled === 'boolean') {
            normalizedTargetPaywalled = targetScopePublishTargetState.targetPaywalled;
        } else {
            normalizedTargetPaywalled = false;
        }

        let normalizedTargetAuthLocked: boolean | null;
        if (currentScopeAccessMetadata.supportsInheritance) {
            if (targetScopePublishTargetState.targetHasAuthLockedField) {
                normalizedTargetAuthLocked = targetScopePublishTargetState.targetAuthLocked ?? false;
            } else {
                normalizedTargetAuthLocked = null;
            }
        } else if (typeof targetScopePublishTargetState.targetAuthLocked === 'boolean') {
            normalizedTargetAuthLocked = targetScopePublishTargetState.targetAuthLocked;
        } else {
            normalizedTargetAuthLocked = false;
        }

        let normalizedTargetLockedUntil: number | null;
        if (currentScopeAccessMetadata.supportsInheritance) {
            if (targetScopePublishTargetState.targetHasLockedUntilField) {
                normalizedTargetLockedUntil = targetScopePublishTargetState.targetLockedUntil ?? null;
            } else {
                normalizedTargetLockedUntil = null;
            }
        } else {
            normalizedTargetLockedUntil = targetScopePublishTargetState.targetLockedUntil ?? null;
        }

        return {
            rawPaywalled: normalizedTargetPaywalled,
            rawAuthLocked: normalizedTargetAuthLocked,
            lockedUntil: normalizedTargetLockedUntil,
            effectivePaywalled: normalizedTargetPaywalled == null
                ? currentScopeAccessMetadata.inheritedPaywalled
                : normalizedTargetPaywalled,
            effectiveAuthLocked: normalizedTargetAuthLocked == null
                ? currentScopeAccessMetadata.inheritedAuthLocked
                : normalizedTargetAuthLocked,
        };
    }, [
        currentScopeAccessMetadata,
        targetScopePublishTargetState,
    ]);
    const accessDraftMatchesTargetState = useMemo(() => (
        !!currentScopeAccessMetadata
        && !!effectiveDraftAccessState
        && !!normalizedTargetAccessState
        && normalizedTargetAccessState.effectivePaywalled === effectiveDraftAccessState.paywalled
        && normalizedTargetAccessState.effectiveAuthLocked === effectiveDraftAccessState.authLocked
        && formatLockedUntilDateInputValue(normalizedTargetAccessState.lockedUntil) === formatLockedUntilDateInputValue(serializedAccessDraft?.lockedUntil ?? null)
    ), [
        currentScopeAccessMetadata,
        effectiveDraftAccessState,
        normalizedTargetAccessState,
        serializedAccessDraft,
    ]);
    const accessPublishWouldBeNoOp = useMemo(() => (
        !accessHasUnsavedChanges
        && accessDraftMatchesTargetState
    ), [
        accessDraftMatchesTargetState,
        accessHasUnsavedChanges,
    ]);
    const accessActionInProgress = savingAccessMetadata || publishingAccessMetadata;

    const previewPublishScopeWarning = useMemo((): string => {
        if (!publishTargetState?.willOverwrite) {
            return '';
        }
        if (currentPublishScope === POST_CONTENT_SCOPE.section) {
            return (
                'If this section already exists in production, preview publish will overwrite the '
                + 'production preview source for this section, leaving the production full-content '
                + 'snapshot unchanged, and only copy missing referenced content media docs and storage assets for this section.'
            );
        }
        if (currentPublishScope === POST_CONTENT_SCOPE.chapter) {
            return (
                'If this chapter already exists in production, preview publish will overwrite the '
                + 'production preview source for this chapter, leaving the production full-content '
                + 'snapshot unchanged, and only copy missing referenced content media docs and storage assets '
                + 'for this chapter and its sections.'
            );
        }
        return (
            'If this post already exists in production, preview publish will overwrite the '
            + 'production preview source for the post, leaving the production full-content snapshot '
            + 'unchanged, and only copy missing referenced content media docs and storage assets for the full post.'
        );
    }, [
        currentPublishScope,
        publishTargetState,
    ]);
    const fullPublishScopeWarning = useMemo((): string => {
        if (!publishTargetState?.willOverwrite) {
            return '';
        }
        if (currentPublishScope === POST_CONTENT_SCOPE.section) {
            return (
                'If this section already exists in production, publishing will overwrite the '
                + 'production section metadata, content, media references, and copied '
                + 'storage assets for this section. Parent post/chapter docs are only created if missing.'
            );
        }
        if (currentPublishScope === POST_CONTENT_SCOPE.chapter) {
            return (
                'If this chapter already exists in production, publishing will overwrite the '
                + 'production chapter metadata, content, media references, and copied '
                + 'storage assets for this chapter and its sections. The root post doc is only created if missing.'
            );
        }
        return (
            'If this post already exists in production, publishing will overwrite the production '
            + 'post metadata, chapters, sections, content, media references, and copied '
            + 'storage assets for the full post.'
        );
    }, [
        currentPublishScope,
        publishTargetState,
    ]);
    const currentScopeRequiresPreviewCutoff = useMemo(() => {
        if (currentHistoryScopeAccessMetadata) {
            return accessMetadataRequiresPreviewCutoff(currentHistoryScopeAccessMetadata);
        }
        return scopeRequiresPreviewCutoffForProductionPublish({
            post,
            selectedPostValuePath: selectedHistoryPostValuePath,
            scope: currentPublishScope,
        });
    }, [
        currentPublishScope,
        currentHistoryScopeAccessMetadata,
        post,
        selectedHistoryPostValuePath,
    ]);
    const currentScopeSupportsPreviewCutoff = useMemo(() => (
        scopeSupportsPreviewCutoffForProductionPublish({
            post,
            selectedPostValuePath: selectedHistoryPostValuePath,
            scope: currentPublishScope,
        })
    ), [
        currentPublishScope,
        post,
        selectedHistoryPostValuePath,
    ]);
    const previewScopeDisabledTooltip = useMemo(() => {
        if (currentScopeSupportsPreviewCutoff) return '';
        if (currentPublishScope === POST_CONTENT_SCOPE.root) {
            return VERSION_HISTORY_PREVIEW_SCOPE_TOOLTIP_ROOT;
        }
        if (currentPublishScope === POST_CONTENT_SCOPE.chapter) {
            return VERSION_HISTORY_PREVIEW_SCOPE_TOOLTIP_CHAPTER;
        }
        return '';
    }, [
        currentPublishScope,
        currentScopeSupportsPreviewCutoff,
    ]);
    const showPreviewControls = isPublishingMode && test && (
        currentScopeRequiresPreviewCutoff
        || !currentScopeSupportsPreviewCutoff
    );
    const showAccessControls = isAccessMode && test && !!currentScopeAccessMetadata;
    const accessSupportsInheritance = !!currentScopeAccessMetadata?.supportsInheritance;
    const inheritedPaywalledLabel = currentScopeAccessMetadata
        ? formatInheritedBooleanLabel(
            currentScopeAccessMetadata.inheritedPaywalled,
            'Paywalled',
            'Free',
        )
        : 'Free';
    const inheritedAuthLockedLabel = currentScopeAccessMetadata
        ? formatInheritedBooleanLabel(
            currentScopeAccessMetadata.inheritedAuthLocked,
            'Auth Locked',
            'Open',
        )
        : 'Open';
    const inheritedLockedUntilLabel = currentScopeAccessMetadata
        ? formatLockedUntilSummary(currentScopeAccessMetadata.inheritedLockedUntil)
        : 'No date';
    const accessCalendarMonthMoment = useMemo(() => {
        const monthFromState = accessCalendarMonth
            ? moment(accessCalendarMonth, 'YYYY-MM-DD', true)
            : moment.invalid();
        if (monthFromState.isValid()) {
            return monthFromState.startOf('month');
        }

        const monthFromValue = accessLockedUntilDate
            ? moment(accessLockedUntilDate, 'YYYY-MM-DD', true)
            : moment.invalid();
        if (monthFromValue.isValid()) {
            return monthFromValue.startOf('month');
        }

        return moment().startOf('month');
    }, [
        accessCalendarMonth,
        accessLockedUntilDate,
    ]);
    const accessCalendarDays = useMemo(() => {
        const gridStart = accessCalendarMonthMoment.clone().startOf('week');
        const today = moment();

        return Array.from({ length: 42 }, (_, index) => {
            const day = gridStart.clone().add(index, 'day');

            return {
                key: day.format('YYYY-MM-DD'),
                label: day.date(),
                value: day.format('YYYY-MM-DD'),
                muted: day.month() !== accessCalendarMonthMoment.month(),
                today: day.isSame(today, 'day'),
                selected: !!accessLockedUntilDate && day.isSame(accessLockedUntilDate, 'day'),
            };
        });
    }, [
        accessCalendarMonthMoment,
        accessLockedUntilDate,
    ]);
    const accessLockedUntilTriggerText = useMemo(() => {
        if (accessLockedUntilMode === LOCKED_UNTIL_MODE.indefinite) {
            return 'No release date';
        }
        if (accessLockedUntilDate) {
            return moment(accessLockedUntilDate, 'YYYY-MM-DD', true).format('MMM D, YYYY');
        }
        return 'mm/dd/yyyy';
    }, [
        accessLockedUntilDate,
        accessLockedUntilMode,
    ]);
    const ACCESS_INFO_TEXT = useMemo(() => ({
        paywall: 'Paywall controls whether this scope requires payment before readers can access it.',
        authLock: 'Auth Lock controls whether readers must be signed in before they can access this scope.',
        lockedUntil: 'Locked Until keeps this scope unavailable until the selected date or until you manually '
            + 'release an indefinite lock, even if its other access settings allow access.',
    }), []);
    const previewPublishRequiresPreviewCutoff = (
        showPreviewControls
        && currentScopeSupportsPreviewCutoff
        && !!selectedCheckpoint
        && !selectedCheckpointHasPreviewCutoff
    );
    const previewPublishRequiresProdTarget = (
        showPreviewControls
        && currentScopeSupportsPreviewCutoff
        && !publishTargetState?.targetCollabDocExists
    );
    const previewCutoffOnlyRequiresProdTarget = (
        showPreviewControls
        && currentScopeSupportsPreviewCutoff
        && !publishTargetState?.targetCollabDocExists
    );
    const previewCutoffOnlyWouldBeNoOp = (
        showPreviewControls
        && currentScopeSupportsPreviewCutoff
        && !!selectedPreviewCutoffParagraphId
        && !!publishTargetState?.targetCollabDocExists
        && publishTargetState.targetPreviewCutoffParagraphId === selectedPreviewCutoffParagraphId
    );
    const previewPublishWouldBeNoOp = (
        showPreviewControls
        && currentScopeSupportsPreviewCutoff
        && !!selectedCheckpoint
        && selectedCheckpointHasPreviewCutoff
        && !!publishTargetState?.targetCollabDocExists
        && publishTargetState.lastPreviewPublishedSourceCheckpointId === selectedCheckpoint.id
        && publishTargetState.targetPreviewCutoffParagraphId === selectedPreviewCutoffParagraphId
    );
    const fullPublishRequiresPreviewCutoff = (
        currentScopeRequiresPreviewCutoff
        && !!selectedCheckpoint
        && !selectedCheckpointHasPreviewCutoff
    );
    const fullPublishWouldBeNoOp = (
        !!selectedCheckpoint
        && !!publishTargetState?.targetCollabDocExists
        && !!publishTargetState?.targetMetadataExists
        && publishTargetState.lastFullPublishedSourceCheckpointId === selectedCheckpoint.id
        && (
            !currentScopeRequiresPreviewCutoff
            || publishTargetState.targetPreviewCutoffParagraphId === selectedPreviewCutoffParagraphId
        )
    );
    let previewPublishDisabledTooltip = '';
    if (!currentScopeSupportsPreviewCutoff) {
        previewPublishDisabledTooltip = previewScopeDisabledTooltip;
    } else if (previewPublishRequiresPreviewCutoff) {
        previewPublishDisabledTooltip = VERSION_HISTORY_PREVIEW_CUTOFF_TOOLTIP;
    } else if (previewPublishRequiresProdTarget) {
        previewPublishDisabledTooltip = VERSION_HISTORY_PREVIEW_PUBLISH_TARGET_TOOLTIP;
    } else if (previewPublishWouldBeNoOp) {
        previewPublishDisabledTooltip = VERSION_HISTORY_PREVIEW_ALREADY_PUBLISHED_TOOLTIP;
    }
    let previewCutoffOnlyDisabledTooltip = '';
    if (!currentScopeSupportsPreviewCutoff) {
        previewCutoffOnlyDisabledTooltip = previewScopeDisabledTooltip;
    } else if (previewPublishRequiresPreviewCutoff) {
        previewCutoffOnlyDisabledTooltip = VERSION_HISTORY_PREVIEW_CUTOFF_TOOLTIP;
    } else if (previewCutoffOnlyRequiresProdTarget) {
        previewCutoffOnlyDisabledTooltip = VERSION_HISTORY_PREVIEW_CUTOFF_ONLY_TOOLTIP;
    } else if (previewCutoffOnlyWouldBeNoOp) {
        previewCutoffOnlyDisabledTooltip = VERSION_HISTORY_PREVIEW_CUTOFF_ALREADY_PUBLISHED_TOOLTIP;
    }
    let setCutoffDisabledTooltip = '';
    if (!currentScopeSupportsPreviewCutoff) {
        setCutoffDisabledTooltip = previewScopeDisabledTooltip;
    }
    let fullPublishDisabledTooltip = '';
    if (fullPublishRequiresPreviewCutoff) {
        fullPublishDisabledTooltip = VERSION_HISTORY_PREVIEW_CUTOFF_TOOLTIP;
    } else if (fullPublishWouldBeNoOp) {
        fullPublishDisabledTooltip = VERSION_HISTORY_FULL_ALREADY_PUBLISHED_TOOLTIP;
    }
    let saveAccessDisabledTooltip = '';
    if (!accessHasUnsavedChanges) {
        saveAccessDisabledTooltip = VERSION_HISTORY_ACCESS_SAVE_TOOLTIP;
    }
    let publishAccessDisabledTooltip = '';
    if (!accessTargetStateLoaded) {
        publishAccessDisabledTooltip = 'Loading production access state for this scope.';
    } else if (accessHasUnsavedChanges) {
        publishAccessDisabledTooltip = VERSION_HISTORY_ACCESS_PUBLISH_UNSAVED_TOOLTIP;
    } else if (accessPublishWouldBeNoOp) {
        publishAccessDisabledTooltip = VERSION_HISTORY_ACCESS_PUBLISH_NOOP_TOOLTIP;
    }
    let saveAndPublishAccessDisabledTooltip = '';
    if (!accessTargetStateLoaded) {
        saveAndPublishAccessDisabledTooltip = 'Loading production access state for this scope.';
    } else if (!accessHasUnsavedChanges) {
        saveAndPublishAccessDisabledTooltip = VERSION_HISTORY_ACCESS_SAVE_AND_PUBLISH_TOOLTIP;
    }

    const restoreScopeMismatch = selectedHistoryDocId !== docId;
    const restoreDisabledTooltip = restoreScopeMismatch
        ? 'Restore only works for the currently open reader scope.'
        : '';

    const restoreModalMessage: IModalMessage = useMemo(() => ({
        title: VERSION_HISTORY_RESTORE_MODAL_TITLE,
        message: VERSION_HISTORY_RESTORE_MODAL_MESSAGE,
    }), []);

    const publishModalMessage: IModalMessage = useMemo(() => ({
        title: publishModalMode === POST_COLLAB_PUBLISH_MODE.preview
            ? VERSION_HISTORY_PUBLISH_PREVIEW_MODAL_TITLE
            : VERSION_HISTORY_PUBLISH_FULL_MODAL_TITLE,
        message: `${
            publishModalMode === POST_COLLAB_PUBLISH_MODE.preview
                ? VERSION_HISTORY_PUBLISH_PREVIEW_MODAL_MESSAGE
                : VERSION_HISTORY_PUBLISH_FULL_MODAL_MESSAGE
        }${
            (
                publishModalMode === POST_COLLAB_PUBLISH_MODE.preview
                    ? previewPublishScopeWarning
                    : fullPublishScopeWarning
            )
                ? ` ${
                    publishModalMode === POST_COLLAB_PUBLISH_MODE.preview
                        ? previewPublishScopeWarning
                        : fullPublishScopeWarning
                }`
                : ''
        }`,
    }), [
        fullPublishScopeWarning,
        publishModalMode,
        previewPublishScopeWarning,
    ]);

    const exportSelectedVersionJson = useCallback(() => {
        if (!post || !selectedCheckpoint || !selectedCheckpointValue) return;

        if (currentSessionId) {
            recordUserAction({
                test,
                sessionId: currentSessionId,
                type: USER_ACTION_TYPE.exportPostCheckpointJson,
                payload: {
                    postId: post.id,
                    docId: selectedCheckpoint.docId || selectedHistoryDocId,
                    checkpointId: selectedCheckpoint.id,
                    version: selectedCheckpoint.version,
                    reason: selectedCheckpoint.reason,
                },
            }).catch(() => null);
        }

        let formattedValue = selectedCheckpointValue;
        try {
            formattedValue = JSON.stringify(JSON.parse(selectedCheckpointValue), null, 2);
        } catch (error) {
            formattedValue = selectedCheckpointValue;
        }

        const blob = new Blob([formattedValue], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${selectedCheckpoint.docId || selectedHistoryDocId || 'post-doc'}-version-${selectedCheckpoint.version}.json`;
        link.click();
        URL.revokeObjectURL(downloadUrl);
    }, [
        currentSessionId,
        selectedHistoryDocId,
        post,
        selectedCheckpoint,
        selectedCheckpointValue,
        test,
    ]);

    const handlePreviewCutoffSelection = useCallback((paragraphId: string) => {
        if (
            !selectedHistoryDocId
            || !selectedCheckpointId
            || savingPreviewCutoff
        ) {
            return;
        }
        const previousParagraphId = selectedPreviewCutoffParagraphId;
        const previousCheckpointId = selectedPreviewCutoffCheckpointId;
        setSelectedPreviewCutoffParagraphId(paragraphId);
        setSelectedPreviewCutoffCheckpointId(selectedCheckpointId);
        setDefinePreviewMode(false);
        setSavingPreviewCutoff(true);
        updatePostCollaborativePreviewCutoff({
            test,
            docId: selectedHistoryDocId,
            previewCutoffParagraphId: paragraphId,
            previewCutoffCheckpointId: selectedCheckpointId,
        }).catch(() => {
            setSelectedPreviewCutoffParagraphId(previousParagraphId);
            setSelectedPreviewCutoffCheckpointId(previousCheckpointId);
            setSnackbarData({
                visible: true,
                text: 'There was a problem saving the preview cutoff.',
                hasFailure: true,
            });
        }).finally(() => {
            setSavingPreviewCutoff(false);
        });
    }, [
        selectedHistoryDocId,
        savingPreviewCutoff,
        selectedCheckpointId,
        selectedPreviewCutoffCheckpointId,
        selectedPreviewCutoffParagraphId,
        setSnackbarData,
        test,
    ]);

    const handlePreviewContainerPointerDownCapture = useCallback((
        e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    ) => {
        if (!definePreviewMode) return;
        const target = e.target as HTMLElement | null;
        const paragraphElement = target?.closest?.('[data-paragraph-id]') as HTMLElement | null;
        const paragraphId = paragraphElement?.getAttribute('data-paragraph-id');
        if (!paragraphId) return;
        e.preventDefault();
        e.stopPropagation();
        handlePreviewCutoffSelection(paragraphId);
    }, [
        definePreviewMode,
        handlePreviewCutoffSelection,
    ]);

    const cancelPreviewCutoffMarkerMeasurement = useCallback((): void => {
        if (previewCutoffMarkerMeasureFrameRef.current == null) {
            return;
        }

        window.cancelAnimationFrame(previewCutoffMarkerMeasureFrameRef.current);
        previewCutoffMarkerMeasureFrameRef.current = null;
    }, []);

    /**
     * Measure the saved cutoff marker after the read-only preview settles so the
     * marker follows delayed heading decorations and chunked preview repaints.
     */
    const measurePreviewCutoffMarkerTop = useCallback((): void => {
        if (
            !visible
            || !currentScopeRequiresPreviewCutoff
            || !currentScopeSupportsPreviewCutoff
            || !selectedCheckpointHasPreviewCutoff
            || !selectedPreviewCutoffParagraphId
            || loadingSelectedCheckpoint
        ) {
            setPreviewCutoffMarkerTop(null);
            return;
        }

        const container = previewContainerRef.current;
        if (!container) {
            setPreviewCutoffMarkerTop(null);
            return;
        }

        const paragraphElement = container.querySelector(
            `[data-paragraph-id="${selectedPreviewCutoffParagraphId}"]`,
        ) as HTMLElement | null;
        if (!paragraphElement) {
            setPreviewCutoffMarkerTop(null);
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const paragraphRect = paragraphElement.getBoundingClientRect();
        setPreviewCutoffMarkerTop(
            paragraphRect.bottom - containerRect.top + container.scrollTop + (0.5 * BODY_FONT_SIZE),
        );
    }, [
        currentScopeRequiresPreviewCutoff,
        currentScopeSupportsPreviewCutoff,
        loadingSelectedCheckpoint,
        selectedCheckpointHasPreviewCutoff,
        selectedPreviewCutoffParagraphId,
        visible,
    ]);

    const schedulePreviewCutoffMarkerMeasurement = useCallback((): void => {
        cancelPreviewCutoffMarkerMeasurement();
        previewCutoffMarkerMeasureFrameRef.current = window.requestAnimationFrame(() => {
            previewCutoffMarkerMeasureFrameRef.current = null;
            measurePreviewCutoffMarkerTop();
        });
    }, [
        cancelPreviewCutoffMarkerMeasurement,
        measurePreviewCutoffMarkerTop,
    ]);

    const handlePreviewBodyPainted = useCallback((): void => {
        schedulePreviewCutoffMarkerMeasurement();
    }, [
        schedulePreviewCutoffMarkerMeasurement,
    ]);

    const handlePreviewRenderedBlockCountChange = useCallback((_blockCount: number): void => {
        schedulePreviewCutoffMarkerMeasurement();
    }, [
        schedulePreviewCutoffMarkerMeasurement,
    ]);

    useEffect(() => {
        if (
            (!currentScopeRequiresPreviewCutoff || !currentScopeSupportsPreviewCutoff)
            && definePreviewMode
        ) {
            setDefinePreviewMode(false);
        }
    }, [
        currentScopeRequiresPreviewCutoff,
        currentScopeSupportsPreviewCutoff,
        definePreviewMode,
    ]);

    useEffect(() => {
        schedulePreviewCutoffMarkerMeasurement();

        return cancelPreviewCutoffMarkerMeasurement;
    }, [
        cancelPreviewCutoffMarkerMeasurement,
        currentScopeRequiresPreviewCutoff,
        currentScopeSupportsPreviewCutoff,
        loadingSelectedCheckpoint,
        schedulePreviewCutoffMarkerMeasurement,
        selectedCheckpointHasPreviewCutoff,
        selectedPreviewCutoffParagraphId,
        visible,
    ]);

    const handleConfirmPublish = useCallback(() => {
        if (
            !selectedHistoryDocId
            || !selectedCheckpoint
            || !publishModalMode
        ) {
            return;
        }
        const currentPublishMode = publishModalMode;
        if (
            currentPublishMode === POST_COLLAB_PUBLISH_MODE.full
            && currentScopeRequiresPreviewCutoff
            && !selectedCheckpointHasPreviewCutoff
        ) {
            setSnackbarData({
                visible: true,
                text: 'Set a preview cutoff before publishing full production content for paywalled or auth-locked scopes.',
                hasFailure: true,
            });
            return;
        }
        setPublishModalMode(null);
        setPublishingMode(currentPublishMode);
        setAnnotationRepairSummary(null);
        setAnnotationRepairSummaryDismissed(false);
        setAnnotationRepairFailedDetailsVisible(false);
        setDetachedAnnotationsRoute(null);
        const publishPreviewCutoffPayload = selectedPreviewCutoffParagraphId != null
            ? {
                previewCutoffParagraphId: selectedPreviewCutoffParagraphId,
                previewCutoffCheckpointId: selectedPreviewCutoffCheckpointId || selectedCheckpoint.id,
            }
            : {};
        const nextDetachedAnnotationsRoute = currentPublishMode === POST_COLLAB_PUBLISH_MODE.full
            ? buildDetachedAnnotationsRoute({
                chapterId: resolvedSelectedHistoryChapterId,
                sectionId: resolvedSelectedHistorySectionId,
            })
            : null;
        publishPostCollaborativeDoc({
            test,
            docId: selectedHistoryDocId,
            checkpointId: selectedCheckpoint.id,
            publishMode: currentPublishMode,
            ...publishPreviewCutoffPayload,
        }).then((result) => {
            let publishSuccessSnackbarText = 'Full version published to production!';
            if (currentPublishMode === POST_COLLAB_PUBLISH_MODE.preview) {
                publishSuccessSnackbarText = 'Preview published to production!';
            } else if (result.annotationRepairSummary) {
                publishSuccessSnackbarText = formatAnnotationRepairSummarySnackbarText(result.annotationRepairSummary);
            }
            const publishedAt = Date.now();
            markSelectedCheckpointPublishedLocally(currentPublishMode, publishedAt);
            setPublishTargetState((currentTargetState) => {
                if (!currentTargetState) return currentTargetState;

                return {
                    ...currentTargetState,
                    targetCollabDocExists: true,
                    ...(currentPublishMode === POST_COLLAB_PUBLISH_MODE.full
                        ? {
                            targetMetadataExists: true,
                            lastFullPublishedSourceCheckpointId: selectedCheckpoint.id,
                            ...(selectedPreviewCutoffParagraphId != null
                                ? {
                                    targetPreviewCutoffParagraphId: selectedPreviewCutoffParagraphId,
                                    lastPreviewPublishedSourceCheckpointId: selectedCheckpoint.id,
                                }
                                : {}),
                        }
                        : {
                            targetPreviewCutoffParagraphId: selectedPreviewCutoffParagraphId || currentTargetState.targetPreviewCutoffParagraphId,
                            lastPreviewPublishedSourceCheckpointId: selectedCheckpoint.id,
                        }),
                };
            });
            if (
                currentPublishMode === POST_COLLAB_PUBLISH_MODE.full
                && result.annotationRepairSummary
            ) {
                setAnnotationRepairSummary(result.annotationRepairSummary);
                setDetachedAnnotationsRoute(nextDetachedAnnotationsRoute);
            }
            if (currentPublishMode === POST_COLLAB_PUBLISH_MODE.full) {
                fetchPublishTargetState(selectedHistoryDocId).then((nextTargetState) => {
                    setPublishTargetState(nextTargetState);
                });
            }
            setSnackbarData({
                visible: true,
                text: publishSuccessSnackbarText,
                hasSuccess: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            });
        }).catch((error) => {
            setSnackbarData({
                visible: true,
                text: resolvePublishFailureSnackbarText({
                    publishMode: currentPublishMode,
                    error,
                }),
                hasFailure: true,
            });
        }).finally(() => {
            setPublishingMode(null);
        });
    }, [
        selectedHistoryDocId,
        publishModalMode,
        markSelectedCheckpointPublishedLocally,
        currentScopeRequiresPreviewCutoff,
        resolvedSelectedHistoryChapterId,
        resolvedSelectedHistorySectionId,
        selectedCheckpoint,
        selectedCheckpointHasPreviewCutoff,
        selectedPreviewCutoffCheckpointId,
        selectedPreviewCutoffParagraphId,
        fetchPublishTargetState,
        setSnackbarData,
        test,
    ]);

    const handleOpenDetachedAnnotationsRoute = useCallback((): void => {
        if (
            !detachedAnnotationsRoute
            || !userIsAdmin
        ) {
            return;
        }

        onClose();
        navigate(detachedAnnotationsRoute);
    }, [
        detachedAnnotationsRoute,
        navigate,
        onClose,
        userIsAdmin,
    ]);

    const handlePublishPreviewCutoffOnly = useCallback(() => {
        if (
            !selectedHistoryDocId
            || !selectedPreviewCutoffParagraphId
            || !publishTargetState?.targetCollabDocExists
            || previewCutoffOnlyWouldBeNoOp
        ) {
            return;
        }
        setPublishingMode(POST_COLLAB_PUBLISH_MODE.preview);
        publishPostCollaborativePreviewCutoff({
            test,
            docId: selectedHistoryDocId,
            previewCutoffParagraphId: selectedPreviewCutoffParagraphId,
            previewCutoffCheckpointId: selectedPreviewCutoffCheckpointId || undefined,
        }).then(() => {
            setPublishTargetState((currentTargetState) => {
                if (!currentTargetState) return currentTargetState;

                return {
                    ...currentTargetState,
                    targetCollabDocExists: true,
                    targetPreviewCutoffParagraphId: selectedPreviewCutoffParagraphId,
                    ...(selectedPreviewCutoffCheckpointId != null
                        ? {
                            lastPreviewPublishedSourceCheckpointId: selectedPreviewCutoffCheckpointId,
                        }
                        : {}),
                };
            });
            setSnackbarData({
                visible: true,
                text: 'Preview cutoff published to production!',
                hasSuccess: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            });
        }).catch(() => {
            setSnackbarData({
                visible: true,
                text: 'There was a problem publishing the preview cutoff.',
                hasFailure: true,
            });
        }).finally(() => {
            setPublishingMode(null);
        });
    }, [
        selectedHistoryDocId,
        publishTargetState,
        previewCutoffOnlyWouldBeNoOp,
        selectedPreviewCutoffCheckpointId,
        selectedPreviewCutoffParagraphId,
        setSnackbarData,
        test,
    ]);

    const handleOpenPublishModal = useCallback((nextPublishMode: POST_COLLAB_PUBLISH_MODE) => {
        if (
            nextPublishMode === POST_COLLAB_PUBLISH_MODE.preview
            && (previewPublishRequiresProdTarget || previewPublishWouldBeNoOp)
        ) {
            if (previewPublishRequiresProdTarget) {
                setSnackbarData({
                    visible: true,
                    text: 'Publish full production content for this scope before publishing a preview.',
                    hasFailure: true,
                });
            }
            return;
        }
        if (
            nextPublishMode === POST_COLLAB_PUBLISH_MODE.full
            && (fullPublishRequiresPreviewCutoff || fullPublishWouldBeNoOp)
        ) {
            if (fullPublishRequiresPreviewCutoff) {
                setSnackbarData({
                    visible: true,
                    text: 'Set a preview cutoff before publishing full production content for paywalled or auth-locked scopes.',
                    hasFailure: true,
                });
            }
            return;
        }
        setPublishModalMode(nextPublishMode);
    }, [
        fullPublishWouldBeNoOp,
        fullPublishRequiresPreviewCutoff,
        previewPublishWouldBeNoOp,
        previewPublishRequiresProdTarget,
        setSnackbarData,
    ]);

    /**
     * Saves the current access draft to staging and mirrors it into local state
     * so a follow-up publish can use the newest values immediately.
     */
    const saveAccessMetadataToStaging = useCallback(async ({
        showSuccessSnackbar = true,
    }: {
        showSuccessSnackbar?: boolean,
    } = {}): Promise<boolean> => {
        if (
            !test
            || !selectedDialogDocId
            || !currentScopeAccessMetadata
            || !serializedAccessDraft
            || !accessHasUnsavedChanges
        ) {
            return false;
        }

        setSavingAccessMetadata(true);
        try {
            await updatePostScopeAccessMetadata({
                test,
                docId: selectedDialogDocId,
                paywalled: serializedAccessDraft.paywalled,
                authLocked: serializedAccessDraft.authLocked,
                lockedUntil: serializedAccessDraft.lockedUntil,
            });
            onSyncScopeAccessMetadata(selectedDialogDocId, serializedAccessDraft);
            setSavedScopeAccessMetadata((currentValue) => (
                currentValue
                    ? {
                        ...currentValue,
                        savedPaywalled: serializedAccessDraft.paywalled,
                        savedAuthLocked: serializedAccessDraft.authLocked,
                        savedLockedUntil: serializedAccessDraft.lockedUntil,
                    }
                    : currentValue
            ));
            if (showSuccessSnackbar) {
                setSnackbarData({
                    visible: true,
                    text: 'Access settings saved to staging!',
                    hasSuccess: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                });
            }
            return true;
        } catch {
            setSnackbarData({
                visible: true,
                text: 'There was a problem saving the access settings.',
                hasFailure: true,
            });
            return false;
        } finally {
            setSavingAccessMetadata(false);
        }
    }, [
        accessHasUnsavedChanges,
        currentScopeAccessMetadata,
        selectedDialogDocId,
        serializedAccessDraft,
        setSavedScopeAccessMetadata,
        setSnackbarData,
        test,
        onSyncScopeAccessMetadata,
    ]);
    const handleSaveAccessMetadata = useCallback(() => {
        setActiveAccessAction(POST_PUBLISHING_DIALOG_ACCESS_ACTION.save);
        saveAccessMetadataToStaging().finally(() => {
            setActiveAccessAction(null);
        });
    }, [
        saveAccessMetadataToStaging,
    ]);

    /**
     * Publishes the current staged access metadata to production and updates the
     * local target snapshot so the dialog reflects the new prod state instantly.
     */
    const publishAccessMetadataToProduction = useCallback(async ({
        successText,
        failureText,
    }: {
        successText: string,
        failureText: string,
    }): Promise<boolean> => {
        if (
            !test
            || !selectedDialogDocId
            || accessPublishWouldBeNoOp
        ) {
            return false;
        }

        setPublishingAccessMetadata(true);
        try {
            await publishPostScopeAccessMetadata({
                test,
                docId: selectedDialogDocId,
            });
            if (serializedAccessDraft) {
                onSyncScopeAccessMetadata(selectedDialogDocId, serializedAccessDraft);
            }
            setTargetScopePublishTargetState((currentTargetState) => (
                currentTargetState
                    ? {
                        ...currentTargetState,
                        targetMetadataExists: true,
                        willOverwrite: true,
                        targetPaywalled: serializedAccessDraft?.paywalled ?? null,
                        targetAuthLocked: serializedAccessDraft?.authLocked ?? null,
                        targetLockedUntil: serializedAccessDraft?.lockedUntil ?? null,
                        targetHasPaywalledField: serializedAccessDraft?.paywalled != null,
                        targetHasAuthLockedField: serializedAccessDraft?.authLocked != null,
                        targetHasLockedUntilField: serializedAccessDraft?.lockedUntil != null,
                    }
                    : currentTargetState
            ));
            setSnackbarData({
                visible: true,
                text: successText,
                hasSuccess: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            });
            return true;
        } catch {
            setSnackbarData({
                visible: true,
                text: failureText,
                hasFailure: true,
            });
            return false;
        } finally {
            setPublishingAccessMetadata(false);
        }
    }, [
        accessPublishWouldBeNoOp,
        selectedDialogDocId,
        serializedAccessDraft,
        setTargetScopePublishTargetState,
        setSnackbarData,
        test,
        onSyncScopeAccessMetadata,
    ]);
    const handlePublishAccessMetadata = useCallback(() => {
        if (accessHasUnsavedChanges) {
            return;
        }

        setActiveAccessAction(POST_PUBLISHING_DIALOG_ACCESS_ACTION.publish);
        publishAccessMetadataToProduction({
            successText: 'Access settings published to production!',
            failureText: 'There was a problem publishing the access settings.',
        }).finally(() => {
            setActiveAccessAction(null);
        });
    }, [
        accessHasUnsavedChanges,
        publishAccessMetadataToProduction,
    ]);
    const handleSaveAndPublishAccessMetadata = useCallback(() => {
        if (
            !test
            || !selectedDialogDocId
            || !accessHasUnsavedChanges
        ) {
            return;
        }

        setActiveAccessAction(POST_PUBLISHING_DIALOG_ACCESS_ACTION.saveAndPublish);
        (async () => {
            const startedWithUnsavedChanges = accessHasUnsavedChanges;

            if (startedWithUnsavedChanges) {
                const savedToStaging = await saveAccessMetadataToStaging({
                    showSuccessSnackbar: false,
                });
                if (!savedToStaging) {
                    return;
                }

                if (accessDraftMatchesTargetState) {
                    setSnackbarData({
                        visible: true,
                        text: VERSION_HISTORY_ACCESS_PUBLISH_SAVE_ONLY_SUCCESS,
                        hasSuccess: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    });
                    return;
                }
            }

            await publishAccessMetadataToProduction({
                successText: startedWithUnsavedChanges
                    ? VERSION_HISTORY_ACCESS_PUBLISH_SUCCESS_WITH_SAVE
                    : 'Access settings published to production!',
                failureText: startedWithUnsavedChanges
                    ? VERSION_HISTORY_ACCESS_PUBLISH_AFTER_SAVE_FAILURE
                    : 'There was a problem publishing the access settings.',
            });
        })().finally(() => {
            setActiveAccessAction(null);
        });
    }, [
        accessDraftMatchesTargetState,
        accessHasUnsavedChanges,
        accessPublishWouldBeNoOp,
        publishAccessMetadataToProduction,
        saveAccessMetadataToStaging,
        selectedDialogDocId,
        setSnackbarData,
        test,
    ]);
    const handleOpenAccessCalendar = useCallback(() => {
        if (accessSupportsInheritance && accessLockedUntilMode === LOCKED_UNTIL_MODE.inherit) {
            return;
        }

        const nextMonth = accessLockedUntilDate
            ? moment(accessLockedUntilDate, 'YYYY-MM-DD', true)
            : moment();
        setAccessLockedUntilMode(LOCKED_UNTIL_MODE.date);
        setAccessCalendarMonth(nextMonth.startOf('month').format('YYYY-MM-DD'));
        setAccessCalendarVisible(true);
    }, [
        accessLockedUntilDate,
        accessLockedUntilMode,
        accessSupportsInheritance,
    ]);
    const handleSelectAccessCalendarDate = useCallback((nextDate: string) => {
        setAccessLockedUntilMode(LOCKED_UNTIL_MODE.date);
        setAccessLockedUntilDate(nextDate);
        setAccessCalendarMonth(moment(nextDate, 'YYYY-MM-DD', true).startOf('month').format('YYYY-MM-DD'));
        setAccessCalendarVisible(false);
    }, []);
    const handleClearAccessCalendarDate = useCallback(() => {
        if (accessSupportsInheritance) {
            setAccessLockedUntilMode(LOCKED_UNTIL_MODE.inherit);
        }
        setAccessLockedUntilDate('');
        setAccessCalendarVisible(false);
    }, [
        accessSupportsInheritance,
    ]);
    const handleSetAccessLockedUntilIndefinite = useCallback(() => {
        setAccessLockedUntilMode(LOCKED_UNTIL_MODE.indefinite);
        setAccessCalendarVisible(false);
    }, []);
    const renderAccessInfoButton = useCallback((
        key: ACCESS_INFO_KEY,
        label: string,
    ): JSX.Element => (
        <HistoryAccessInfoButton
            className={HOVER_TARGET_CLASSNAME}
            customCursorActive={customCursorActive}
            aria-label={label}
            aria-expanded={activeAccessInfoKeys[key]}
            onMouseEnter={(e) => onCursorEnter(
                CURSOR_TARGET.editorButton,
                ['click'],
                e.currentTarget as HTMLElement,
            )}
            onMouseLeave={onCursorLeave}
            {...getPressEventHandlers({
                onTouchStart: () => setActiveAccessInfoKeys((currentValue) => ({
                    ...currentValue,
                    [key]: !currentValue[key],
                })),
                onMouseDown: () => setActiveAccessInfoKeys((currentValue) => ({
                    ...currentValue,
                    [key]: !currentValue[key],
                })),
            })}
        >
            <HistoryAccessInfoIcon src={InfoIcon} alt="" />
        </HistoryAccessInfoButton>
    ), [
        activeAccessInfoKeys,
        customCursorActive,
        onCursorEnter,
        onCursorLeave,
    ]);

    const handleConfirmRestore = useCallback(() => {
        if (!post || !selectedCheckpointValue) return;

        if (currentSessionId && selectedCheckpoint) {
            recordUserAction({
                test,
                sessionId: currentSessionId,
                type: USER_ACTION_TYPE.restorePostCheckpoint,
                payload: {
                    postId: post.id,
                    docId: selectedCheckpoint.docId || selectedHistoryDocId,
                    checkpointId: selectedCheckpoint.id,
                    version: selectedCheckpoint.version,
                    reason: selectedCheckpoint.reason,
                },
            }).catch(() => null);
        }

        onRestore(selectedCheckpointValue);
        setRestoreModalVisible(false);
        onClose();
    }, [
        currentSessionId,
        selectedHistoryDocId,
        onClose,
        onRestore,
        post,
        selectedCheckpoint,
        selectedCheckpointValue,
        test,
    ]);

    // ===== Renderers ======

    const HistorySidebarContent = isPublishingMode ? (
        <HistorySidebar>
            <HistorySidebarControls>
                <HistoryScopeRow data-testid="version-history-scope-row">
                    <HistoryScopeLabel>History</HistoryScopeLabel>
                    {historyRootAvailable && (
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.root
                                ? themeObj.verascopeColor.purple300
                                : themeObj.color.cream400}
                            text="Post"
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => setSelectedHistoryScope(POST_CONTENT_SCOPE.root),
                                onMouseDown: () => setSelectedHistoryScope(POST_CONTENT_SCOPE.root),
                            })}
                        />
                    )}
                    {historyChapterOptions.length > 0 && (
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.chapter
                                ? themeObj.verascopeColor.purple300
                                : themeObj.color.cream400}
                            text="Chapter"
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => {
                                    if (resolvedSelectedHistoryChapterId || historyChapterOptions[0]?.id) {
                                        setSelectedHistoryChapterId(resolvedSelectedHistoryChapterId || historyChapterOptions[0].id);
                                        setSelectedHistoryScope(POST_CONTENT_SCOPE.chapter);
                                    }
                                },
                                onMouseDown: () => {
                                    if (resolvedSelectedHistoryChapterId || historyChapterOptions[0]?.id) {
                                        setSelectedHistoryChapterId(resolvedSelectedHistoryChapterId || historyChapterOptions[0].id);
                                        setSelectedHistoryScope(POST_CONTENT_SCOPE.chapter);
                                    }
                                },
                            })}
                        />
                    )}
                    {canShowHistorySectionButton && (
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section
                                ? themeObj.verascopeColor.purple300
                                : themeObj.color.cream400}
                            text="Section"
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => {
                                    const nextChapterId = resolvedSelectedHistoryChapterId || historySectionChapterOptions[0]?.id;
                                    const nextChapter = historySectionChapterOptions.find((chapter) => chapter.id === nextChapterId)
                                        || historySectionChapterOptions[0]
                                        || null;
                                    const nextSectionId = resolvedSelectedHistorySectionId || nextChapter?.sections?.[0]?.id || null;
                                    if (nextChapterId && nextSectionId) {
                                        setSelectedHistoryChapterId(nextChapterId);
                                        setSelectedHistorySectionId(nextSectionId);
                                        setSelectedHistoryScope(POST_CONTENT_SCOPE.section);
                                    }
                                },
                                onMouseDown: () => {
                                    const nextChapterId = resolvedSelectedHistoryChapterId || historySectionChapterOptions[0]?.id;
                                    const nextChapter = historySectionChapterOptions.find((chapter) => chapter.id === nextChapterId)
                                        || historySectionChapterOptions[0]
                                        || null;
                                    const nextSectionId = resolvedSelectedHistorySectionId || nextChapter?.sections?.[0]?.id || null;
                                    if (nextChapterId && nextSectionId) {
                                        setSelectedHistoryChapterId(nextChapterId);
                                        setSelectedHistorySectionId(nextSectionId);
                                        setSelectedHistoryScope(POST_CONTENT_SCOPE.section);
                                    }
                                },
                            })}
                        />
                    )}
                </HistoryScopeRow>
                {(resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.chapter || resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section) && (
                    <HistoryScopePathRow>
                        <HistoryScopeSelect
                            $compact
                            $sidebar
                            customCursorActive={customCursorActive}
                            className={HOVER_TARGET_CLASSNAME}
                            aria-label="Version history chapter scope"
                            value={resolvedSelectedHistoryChapterId || ''}
                            onMouseEnter={(e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            )}
                            onMouseLeave={onCursorLeave}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                const nextChapterId = e.target.value || null;
                                setSelectedHistoryChapterId(nextChapterId);
                                const nextChapter = postChapters.find((chapter) => chapter.id === nextChapterId) || null;
                                const nextSectionId = nextChapter?.sections?.[0]?.id || null;
                                setSelectedHistorySectionId(nextSectionId);
                                setSelectedHistoryScope(
                                    resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section
                                        && nextSectionId
                                        ? POST_CONTENT_SCOPE.section
                                        : POST_CONTENT_SCOPE.chapter,
                                );
                            }}
                        >
                            {(
                                resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section
                                    ? historySectionChapterOptions
                                    : historyChapterOptions
                            ).map((chapter) => (
                                <option
                                    key={chapter.id}
                                    value={chapter.id}
                                >
                                    {chapter.title}
                                </option>
                            ))}
                        </HistoryScopeSelect>
                        {resolvedSelectedHistoryScope === POST_CONTENT_SCOPE.section && selectedHistoryChapterSections.length > 0 && (
                            <>
                                <HistoryScopeSeparator>/</HistoryScopeSeparator>
                                <HistoryScopeSelect
                                    $compact
                                    $sidebar
                                    customCursorActive={customCursorActive}
                                    className={HOVER_TARGET_CLASSNAME}
                                    aria-label="Version history section scope"
                                    value={resolvedSelectedHistorySectionId || ''}
                                    onMouseEnter={(e) => onCursorEnter(
                                        CURSOR_TARGET.editorButton,
                                        ['click'],
                                        e.currentTarget as HTMLElement,
                                    )}
                                    onMouseLeave={onCursorLeave}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                        setSelectedHistorySectionId(e.target.value || null);
                                        setSelectedHistoryScope(POST_CONTENT_SCOPE.section);
                                    }}
                                >
                                    {selectedHistoryChapterSections.map((section) => (
                                        <option
                                            key={section.id}
                                            value={section.id}
                                        >
                                            {section.title}
                                        </option>
                                    ))}
                                </HistoryScopeSelect>
                            </>
                        )}
                    </HistoryScopePathRow>
                )}
            </HistorySidebarControls>
            <HistorySidebarList>
                {loadingCheckpoints && (
                    <HistoryLoadingState>
                        <Spinner />
                    </HistoryLoadingState>
                )}
                {checkpointLoadFailed && (
                    <HistoryErrorState>
                        Could not load saved history.
                    </HistoryErrorState>
                )}
                {!loadingCheckpoints && !checkpointLoadFailed && checkpoints.length === 0 && (
                    <HistoryEmptyState>
                        No saved history yet.
                    </HistoryEmptyState>
                )}
                {!checkpointLoadFailed && checkpoints.map((checkpoint, index) => {
                    const isSelected = checkpoint.id === selectedCheckpointId;
                    const isLatest = index === 0;
                    const isPublishedPreview = (
                        checkpoint.publishedToProductionMode === POST_COLLAB_PUBLISH_MODE.preview
                    );
                    const isPublishedFull = (
                        checkpoint.publishedToProductionMode === POST_COLLAB_PUBLISH_MODE.full
                        || checkpoint.reason === POST_COLLAB_DOC_CHECKPOINT_REASON.publish
                    );
                    const isPublishedCutoff = (
                        !!publishTargetState?.targetPreviewCutoffParagraphId
                        && (
                            publishTargetState.lastPreviewPublishedSourceCheckpointId === checkpoint.id
                            || (
                                checkpoint.id === selectedCheckpointId
                                && selectedCheckpointHasPreviewCutoff
                                && publishTargetState.targetPreviewCutoffParagraphId === selectedPreviewCutoffParagraphId
                            )
                        )
                    );
                    return (
                        <HistoryListItem
                            key={checkpoint.id}
                            className={HOVER_TARGET_CLASSNAME}
                            selected={isSelected}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => {
                                    setDefinePreviewMode(false);
                                    setSelectedCheckpointId(checkpoint.id);
                                },
                                onMouseDown: () => {
                                    setDefinePreviewMode(false);
                                    setSelectedCheckpointId(checkpoint.id);
                                },
                            })}
                        >
                            <HistoryTimestamp>
                                {moment.utc(checkpoint.createdAt).local().format('MMM D, YYYY h:mm:ss A')}
                            </HistoryTimestamp>
                            <HistoryMetaRow>
                                <HistoryVersionLabel>
                                    Version
                                    {' '}
                                    {checkpoint.version}
                                </HistoryVersionLabel>
                                {isLatest && <HistoryTag>Current</HistoryTag>}
                                {isPublishedPreview && <HistoryTag published>Preview</HistoryTag>}
                                {isPublishedCutoff && <HistoryTag published>Cutoff</HistoryTag>}
                                {isPublishedFull && <HistoryTag published>Full</HistoryTag>}
                            </HistoryMetaRow>
                        </HistoryListItem>
                    );
                })}
            </HistorySidebarList>
        </HistorySidebar>
    ) : null;

    const AccessControlsContent = (
        <>
            {showAccessControls && (
                <HistoryAccessSection data-testid="publish-target-scope-row">
                    <HistoryScopeLabel>Target</HistoryScopeLabel>
                    <HistoryTargetRow $separated={false}>
                        <HistoryTargetControls>
                            <HistoryTargetButtonGroup>
                                <Button
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    type={BUTTON_TYPE.solid}
                                    background={selectedDialogScope === POST_CONTENT_SCOPE.root
                                        ? themeObj.verascopeColor.purple300
                                        : themeObj.color.cream400}
                                    text="Post"
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: (e) => onCursorEnter(
                                            CURSOR_TARGET.editorButton,
                                            ['click'],
                                            e.currentTarget as HTMLElement,
                                        ),
                                        onMouseLeave: onCursorLeave,
                                    })}
                                    {...getPressEventHandlers({
                                        onTouchStart: () => setSelectedDialogScope(POST_CONTENT_SCOPE.root),
                                        onMouseDown: () => setSelectedDialogScope(POST_CONTENT_SCOPE.root),
                                    })}
                                />
                                {postChapters.length > 0 && (
                                    <Button
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        type={BUTTON_TYPE.solid}
                                        background={selectedDialogScope === POST_CONTENT_SCOPE.chapter
                                            ? themeObj.verascopeColor.purple300
                                            : themeObj.color.cream400}
                                        text="Chapter"
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: (e) => onCursorEnter(
                                                CURSOR_TARGET.editorButton,
                                                ['click'],
                                                e.currentTarget as HTMLElement,
                                            ),
                                            onMouseLeave: onCursorLeave,
                                        })}
                                        {...getPressEventHandlers({
                                            onTouchStart: () => {
                                                if (selectedDialogChapterId || postChapters[0]?.id) {
                                                    setSelectedDialogChapterId(selectedDialogChapterId || postChapters[0].id);
                                                    setSelectedDialogScope(POST_CONTENT_SCOPE.chapter);
                                                }
                                            },
                                            onMouseDown: () => {
                                                if (selectedDialogChapterId || postChapters[0]?.id) {
                                                    setSelectedDialogChapterId(selectedDialogChapterId || postChapters[0].id);
                                                    setSelectedDialogScope(POST_CONTENT_SCOPE.chapter);
                                                }
                                            },
                                        })}
                                    />
                                )}
                                {canShowTargetSectionButton && (
                                    <Button
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        type={BUTTON_TYPE.solid}
                                        background={selectedDialogScope === POST_CONTENT_SCOPE.section
                                            ? themeObj.verascopeColor.purple300
                                            : themeObj.color.cream400}
                                        text="Section"
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: (e) => onCursorEnter(
                                                CURSOR_TARGET.editorButton,
                                                ['click'],
                                                e.currentTarget as HTMLElement,
                                            ),
                                            onMouseLeave: onCursorLeave,
                                        })}
                                        {...getPressEventHandlers({
                                            onTouchStart: () => {
                                                if (selectedDialogSectionId || selectedDialogChapterSections[0]?.id) {
                                                    setSelectedDialogSectionId(selectedDialogSectionId || selectedDialogChapterSections[0].id);
                                                    setSelectedDialogScope(POST_CONTENT_SCOPE.section);
                                                }
                                            },
                                            onMouseDown: () => {
                                                if (selectedDialogSectionId || selectedDialogChapterSections[0]?.id) {
                                                    setSelectedDialogSectionId(selectedDialogSectionId || selectedDialogChapterSections[0].id);
                                                    setSelectedDialogScope(POST_CONTENT_SCOPE.section);
                                                }
                                            },
                                        })}
                                    />
                                )}
                            </HistoryTargetButtonGroup>
                            {(selectedDialogScope === POST_CONTENT_SCOPE.chapter || selectedDialogScope === POST_CONTENT_SCOPE.section) && postChapters.length > 0 && (
                                <HistoryTargetSelectGroup>
                                    <HistoryScopeSelect
                                        $compact
                                        $fill
                                        customCursorActive={customCursorActive}
                                        className={HOVER_TARGET_CLASSNAME}
                                        aria-label="Publish target chapter scope"
                                        value={selectedDialogChapterId || ''}
                                        onMouseEnter={(e) => onCursorEnter(
                                            CURSOR_TARGET.editorButton,
                                            ['click'],
                                            e.currentTarget as HTMLElement,
                                        )}
                                        onMouseLeave={onCursorLeave}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                            const nextChapterId = e.target.value || null;
                                            setSelectedDialogChapterId(nextChapterId);
                                            const nextChapter = postChapters.find((chapter) => chapter.id === nextChapterId) || null;
                                            const nextSectionId = nextChapter?.sections?.[0]?.id || null;
                                            setSelectedDialogSectionId(nextSectionId);
                                            setSelectedDialogScope(
                                                selectedDialogScope === POST_CONTENT_SCOPE.section
                                                    && nextSectionId
                                                    ? POST_CONTENT_SCOPE.section
                                                    : POST_CONTENT_SCOPE.chapter,
                                            );
                                        }}
                                    >
                                        {postChapters.map((chapter) => (
                                            <option key={chapter.id} value={chapter.id}>
                                                {chapter.title}
                                            </option>
                                        ))}
                                    </HistoryScopeSelect>
                                    {selectedDialogScope === POST_CONTENT_SCOPE.section && selectedDialogChapterSections.length > 0 && (
                                        <>
                                            <HistoryScopeSeparator>/</HistoryScopeSeparator>
                                            <HistoryScopeSelect
                                                $compact
                                                $fill
                                                customCursorActive={customCursorActive}
                                                className={HOVER_TARGET_CLASSNAME}
                                                aria-label="Publish target section scope"
                                                value={selectedDialogSectionId || ''}
                                                onMouseEnter={(e) => onCursorEnter(
                                                    CURSOR_TARGET.editorButton,
                                                    ['click'],
                                                    e.currentTarget as HTMLElement,
                                                )}
                                                onMouseLeave={onCursorLeave}
                                                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                                    setSelectedDialogSectionId(e.target.value || null);
                                                    setSelectedDialogScope(POST_CONTENT_SCOPE.section);
                                                }}
                                            >
                                                {selectedDialogChapterSections.map((section) => (
                                                    <option key={section.id} value={section.id}>
                                                        {section.title}
                                                    </option>
                                                ))}
                                            </HistoryScopeSelect>
                                        </>
                                    )}
                                </HistoryTargetSelectGroup>
                            )}
                        </HistoryTargetControls>
                    </HistoryTargetRow>
                </HistoryAccessSection>
            )}
            {showAccessControls && currentScopeAccessMetadata && (
                <HistoryAccessSection>
                    <HistoryAccessGrid>
                        <HistoryAccessField>
                            <HistoryAccessLabelRow>
                                <HistoryAccessLabel>Paywall</HistoryAccessLabel>
                                {renderAccessInfoButton(ACCESS_INFO_KEY.paywall, 'Explain paywall')}
                            </HistoryAccessLabelRow>
                            {activeAccessInfoKeys[ACCESS_INFO_KEY.paywall] && (
                                <HistoryAccessInfoText>
                                    {ACCESS_INFO_TEXT.paywall}
                                </HistoryAccessInfoText>
                            )}
                            <ToggleButton
                                customCursorActive={customCursorActive}
                                hasSound={hasSound}
                                selectedIndex={accessSupportsInheritance
                                    ? [ACCESS_TOGGLE_MODE.inherit, ACCESS_TOGGLE_MODE.off, ACCESS_TOGGLE_MODE.on].indexOf(accessPaywalledMode)
                                    : [ACCESS_TOGGLE_MODE.off, ACCESS_TOGGLE_MODE.on].indexOf(accessPaywalledMode)}
                                items={accessSupportsInheritance
                                    ? [
                                        { text: `Inherit: ${inheritedPaywalledLabel}`, handleClick: () => setAccessPaywalledMode(ACCESS_TOGGLE_MODE.inherit) },
                                        { text: 'Off', handleClick: () => setAccessPaywalledMode(ACCESS_TOGGLE_MODE.off) },
                                        { text: 'On', handleClick: () => setAccessPaywalledMode(ACCESS_TOGGLE_MODE.on) },
                                    ]
                                    : [
                                        { text: 'Off', handleClick: () => setAccessPaywalledMode(ACCESS_TOGGLE_MODE.off) },
                                        { text: 'On', handleClick: () => setAccessPaywalledMode(ACCESS_TOGGLE_MODE.on) },
                                    ]}
                                buttonWidth={accessSupportsInheritance ? 112 : 76}
                                buttonHeight={28}
                                buttonMargin={4}
                                transitionDuration={160}
                                background={themeObj.color.neutral300}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                darkBackground={false}
                            />
                        </HistoryAccessField>
                        <HistoryAccessField>
                            <HistoryAccessLabelRow>
                                <HistoryAccessLabel>Auth Lock</HistoryAccessLabel>
                                {renderAccessInfoButton(ACCESS_INFO_KEY.authLock, 'Explain auth lock')}
                            </HistoryAccessLabelRow>
                            {activeAccessInfoKeys[ACCESS_INFO_KEY.authLock] && (
                                <HistoryAccessInfoText>
                                    {ACCESS_INFO_TEXT.authLock}
                                </HistoryAccessInfoText>
                            )}
                            <ToggleButton
                                customCursorActive={customCursorActive}
                                hasSound={hasSound}
                                selectedIndex={accessSupportsInheritance
                                    ? [ACCESS_TOGGLE_MODE.inherit, ACCESS_TOGGLE_MODE.off, ACCESS_TOGGLE_MODE.on].indexOf(accessAuthLockedMode)
                                    : [ACCESS_TOGGLE_MODE.off, ACCESS_TOGGLE_MODE.on].indexOf(accessAuthLockedMode)}
                                items={accessSupportsInheritance
                                    ? [
                                        { text: `Inherit: ${inheritedAuthLockedLabel}`, handleClick: () => setAccessAuthLockedMode(ACCESS_TOGGLE_MODE.inherit) },
                                        { text: 'Off', handleClick: () => setAccessAuthLockedMode(ACCESS_TOGGLE_MODE.off) },
                                        { text: 'On', handleClick: () => setAccessAuthLockedMode(ACCESS_TOGGLE_MODE.on) },
                                    ]
                                    : [
                                        { text: 'Off', handleClick: () => setAccessAuthLockedMode(ACCESS_TOGGLE_MODE.off) },
                                        { text: 'On', handleClick: () => setAccessAuthLockedMode(ACCESS_TOGGLE_MODE.on) },
                                    ]}
                                buttonWidth={accessSupportsInheritance ? 116 : 76}
                                buttonHeight={28}
                                buttonMargin={4}
                                transitionDuration={160}
                                background={themeObj.color.neutral300}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                darkBackground={false}
                            />
                        </HistoryAccessField>
                        <HistoryAccessField $spanFull>
                            <HistoryAccessLabelRow>
                                <HistoryAccessLabel>Locked until</HistoryAccessLabel>
                                {renderAccessInfoButton(ACCESS_INFO_KEY.lockedUntil, 'Explain locked until')}
                            </HistoryAccessLabelRow>
                            {activeAccessInfoKeys[ACCESS_INFO_KEY.lockedUntil] && (
                                <HistoryAccessInfoText>
                                    {ACCESS_INFO_TEXT.lockedUntil}
                                </HistoryAccessInfoText>
                            )}
                            <HistoryAccessDateRow>
                                <ToggleButton
                                    customCursorActive={customCursorActive}
                                    hasSound={hasSound}
                                    selectedIndex={accessSupportsInheritance
                                        ? [
                                            LOCKED_UNTIL_MODE.inherit,
                                            LOCKED_UNTIL_MODE.date,
                                            LOCKED_UNTIL_MODE.indefinite,
                                        ].indexOf(accessLockedUntilMode)
                                        : [LOCKED_UNTIL_MODE.date, LOCKED_UNTIL_MODE.indefinite].indexOf(accessLockedUntilMode)}
                                    items={accessSupportsInheritance
                                        ? [
                                            {
                                                text: `Inherit: ${inheritedLockedUntilLabel}`,
                                                handleClick: () => setAccessLockedUntilMode(LOCKED_UNTIL_MODE.inherit),
                                            },
                                            {
                                                text: 'Set Date',
                                                handleClick: () => setAccessLockedUntilMode(LOCKED_UNTIL_MODE.date),
                                            },
                                            { text: 'Indefinite', handleClick: handleSetAccessLockedUntilIndefinite },
                                        ]
                                        : [
                                            {
                                                text: 'Set Date',
                                                handleClick: () => setAccessLockedUntilMode(LOCKED_UNTIL_MODE.date),
                                            },
                                            { text: 'Indefinite', handleClick: handleSetAccessLockedUntilIndefinite },
                                        ]}
                                    buttonWidth={138}
                                    buttonHeight={28}
                                    buttonMargin={4}
                                    transitionDuration={160}
                                    background={themeObj.color.neutral300}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    darkBackground={false}
                                />
                                <HistoryAccessDateField ref={accessCalendarRef}>
                                    <HistoryAccessDateTrigger
                                        className={HOVER_TARGET_CLASSNAME}
                                        aria-label="Locked Until"
                                        disabled={accessLockedUntilMode !== LOCKED_UNTIL_MODE.date}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: (e) => onCursorEnter(
                                                CURSOR_TARGET.editorButton,
                                                ['click'],
                                                e.currentTarget as HTMLElement,
                                            ),
                                            onMouseLeave: onCursorLeave,
                                        })}
                                        {...getPressEventHandlers({
                                            onTouchStart: handleOpenAccessCalendar,
                                            onMouseDown: handleOpenAccessCalendar,
                                        })}
                                    >
                                        {accessLockedUntilTriggerText}
                                    </HistoryAccessDateTrigger>
                                    {accessCalendarVisible && (
                                        <HistoryAccessDatePopover>
                                            <HistoryAccessCalendarHeader>
                                                <HistoryAccessCalendarNavButton
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    type="button"
                                                    aria-label="Previous month"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: (e) => onCursorEnter(
                                                            CURSOR_TARGET.editorButton,
                                                            ['click'],
                                                            e.currentTarget as HTMLElement,
                                                        ),
                                                        onMouseLeave: onCursorLeave,
                                                    })}
                                                    {...getPressEventHandlers({
                                                        onTouchStart: () => setAccessCalendarMonth(accessCalendarMonthMoment.clone().subtract(1, 'month').format('YYYY-MM-DD')),
                                                        onMouseDown: () => setAccessCalendarMonth(accessCalendarMonthMoment.clone().subtract(1, 'month').format('YYYY-MM-DD')),
                                                    })}
                                                >
                                                    ‹
                                                </HistoryAccessCalendarNavButton>
                                                <HistoryAccessCalendarTitle>
                                                    {accessCalendarMonthMoment.format('MMMM YYYY')}
                                                </HistoryAccessCalendarTitle>
                                                <HistoryAccessCalendarNavButton
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    type="button"
                                                    aria-label="Next month"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: (e) => onCursorEnter(
                                                            CURSOR_TARGET.editorButton,
                                                            ['click'],
                                                            e.currentTarget as HTMLElement,
                                                        ),
                                                        onMouseLeave: onCursorLeave,
                                                    })}
                                                    {...getPressEventHandlers({
                                                        onTouchStart: () => setAccessCalendarMonth(accessCalendarMonthMoment.clone().add(1, 'month').format('YYYY-MM-DD')),
                                                        onMouseDown: () => setAccessCalendarMonth(accessCalendarMonthMoment.clone().add(1, 'month').format('YYYY-MM-DD')),
                                                    })}
                                                >
                                                    ›
                                                </HistoryAccessCalendarNavButton>
                                            </HistoryAccessCalendarHeader>
                                            <HistoryAccessCalendarWeekdays>
                                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday) => (
                                                    <HistoryAccessCalendarWeekday key={weekday}>
                                                        {weekday}
                                                    </HistoryAccessCalendarWeekday>
                                                ))}
                                            </HistoryAccessCalendarWeekdays>
                                            <HistoryAccessCalendarGrid>
                                                {accessCalendarDays.map((day) => (
                                                    <HistoryAccessCalendarDay
                                                        key={day.key}
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        type="button"
                                                        $selected={day.selected}
                                                        $muted={day.muted}
                                                        $today={day.today}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: (e) => onCursorEnter(
                                                                CURSOR_TARGET.editorButton,
                                                                ['click'],
                                                                e.currentTarget as HTMLElement,
                                                            ),
                                                            onMouseLeave: onCursorLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => handleSelectAccessCalendarDate(day.value),
                                                            onMouseDown: () => handleSelectAccessCalendarDate(day.value),
                                                        })}
                                                    >
                                                        {day.label}
                                                    </HistoryAccessCalendarDay>
                                                ))}
                                            </HistoryAccessCalendarGrid>
                                            {!!accessLockedUntilDate && (
                                                <HistoryAccessCalendarFooter>
                                                    <HistoryAccessCalendarActionButton
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        type="button"
                                                        onMouseEnter={(e) => onCursorEnter(
                                                            CURSOR_TARGET.editorButton,
                                                            ['click'],
                                                            e.currentTarget as HTMLElement,
                                                        )}
                                                        onMouseLeave={onCursorLeave}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: handleClearAccessCalendarDate,
                                                            onMouseDown: handleClearAccessCalendarDate,
                                                        })}
                                                    >
                                                        Clear
                                                    </HistoryAccessCalendarActionButton>
                                                </HistoryAccessCalendarFooter>
                                            )}
                                        </HistoryAccessDatePopover>
                                    )}
                                </HistoryAccessDateField>
                            </HistoryAccessDateRow>
                        </HistoryAccessField>
                    </HistoryAccessGrid>
                </HistoryAccessSection>
            )}
            {showAccessControls && currentScopeAccessMetadata && (
                <HistoryAccessSection>
                    <HistoryAccessActions>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={themeObj.verascopeColor.purple400}
                            text="Save"
                            icon={SaveIcon}
                            loading={savingAccessMetadata && activeAccessAction === POST_PUBLISHING_DIALOG_ACCESS_ACTION.save}
                            disabled={!accessHasUnsavedChanges || accessActionInProgress}
                            tooltip={{
                                active: !!saveAccessDisabledTooltip,
                                text: saveAccessDisabledTooltip,
                                side: TOOLTIP_TYPE.top,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: handleSaveAccessMetadata,
                                onMouseDown: handleSaveAccessMetadata,
                            })}
                        />
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={themeObj.verascopeColor.orange200}
                            text="Publish"
                            icon={PaperplaneIcon}
                            loading={publishingAccessMetadata && activeAccessAction === POST_PUBLISHING_DIALOG_ACCESS_ACTION.publish}
                            disabled={!accessTargetStateLoaded || accessActionInProgress || accessHasUnsavedChanges || accessPublishWouldBeNoOp}
                            tooltip={{
                                active: !!publishAccessDisabledTooltip,
                                text: publishAccessDisabledTooltip,
                                side: TOOLTIP_TYPE.top,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: handlePublishAccessMetadata,
                                onMouseDown: handlePublishAccessMetadata,
                            })}
                        />
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={themeObj.verascopeColor.orange200}
                            text="Save and Publish"
                            icon={PaperplaneIcon}
                            loading={activeAccessAction === POST_PUBLISHING_DIALOG_ACCESS_ACTION.saveAndPublish && accessActionInProgress}
                            disabled={!accessTargetStateLoaded || accessActionInProgress || !accessHasUnsavedChanges}
                            tooltip={{
                                active: !!saveAndPublishAccessDisabledTooltip,
                                text: saveAndPublishAccessDisabledTooltip,
                                side: TOOLTIP_TYPE.top,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.editorButton,
                                    ['click'],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: handleSaveAndPublishAccessMetadata,
                                onMouseDown: handleSaveAndPublishAccessMetadata,
                            })}
                        />
                    </HistoryAccessActions>
                </HistoryAccessSection>
            )}
        </>
    );

    const SelectedVersionPreviewContent = isPublishingMode ? (
        <HistoryPreviewContainer
            ref={previewContainerRef}
            data-testid="version-history-preview-container"
            onMouseDownCapture={handlePreviewContainerPointerDownCapture}
            onTouchStartCapture={handlePreviewContainerPointerDownCapture}
        >
            {showPreviewControls && previewCutoffMarkerTop != null && (
                <HistoryPreviewCutoffMarker
                    top={previewCutoffMarkerTop}
                    data-testid="version-history-preview-cutoff-marker"
                >
                    <HistoryPreviewCutoffLine />
                    <HistoryPreviewCutoffLabel>
                        Preview ends here
                    </HistoryPreviewCutoffLabel>
                    <HistoryPreviewCutoffLine />
                </HistoryPreviewCutoffMarker>
            )}
            {loadingSelectedCheckpoint && (
                <HistoryLoadingState>
                    <Spinner large lightBackground />
                </HistoryLoadingState>
            )}
            {!loadingSelectedCheckpoint && !!selectedCheckpointValue && (
                <ErrorBoundary
                    resetKeys={[
                        selectedCheckpoint?.id || 'version-history-preview',
                        selectedHistoryDocId || 'unknown-doc',
                        selectedHistoryPathKey,
                    ]}
                    onError={(error) => {
                        logVersionHistoryError({
                            message: 'Version history preview render failed.',
                            error,
                            checkpointId: selectedCheckpoint?.id || null,
                        });
                    }}
                    fallbackRender={renderVersionHistoryPreviewFallback}
                >
                    <PostEditor
                        key={selectedCheckpoint?.id || 'version-history-preview'}
                        customCursorActive={customCursorActive}
                        readOnly
                        user={user || null}
                        userIsAdmin={userIsAdmin}
                        isAuthor={!!user && !!post?.authors.includes(user.id)}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId || null}
                        color={DEFAULT_EDITOR_COLOR}
                        id={post?.id || ''}
                        type={EDITOR_CONTEXT_TYPE.post}
                        postValue={selectedCheckpointValue !== ''
                            ? selectedCheckpointValue
                            : (currentPostValue || '')}
                        placeholder=""
                        showHeaderTwoPartMarkers
                        bufferHeight={0}
                        parentRef={previewContainerRef.current}
                        scrollParentRef={previewContainerRef}
                        onBodyPainted={handlePreviewBodyPainted}
                        onReadOnlyRenderedBlockCountChange={handlePreviewRenderedBlockCountChange}
                        acceptedFiles={EMPTY_FILES}
                        uploadingMedia={EMPTY_MEDIA_MAP}
                        setUploadingMedia={() => undefined}
                        updateUploadingMedia={() => undefined}
                        removeUploadingMedia={() => undefined}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={EMPTY_INPUT_FOCUS_SETTER}
                        savePost={() => undefined}
                        postValueIsSaving={false}
                        setEditor={EMPTY_EDITOR_SETTER}
                        postQuote={null}
                        annotations={[]}
                        setAnnotations={EMPTY_ANNOTATIONS_MAP_SETTER}
                        chapters={post?.chapters || []}
                        setAnnotationHighlightsRendered={EMPTY_BOOLEAN_SETTER}
                        annotationHighlightsRendered={false}
                        setRemeasurePostHeight={EMPTY_BOOLEAN_SETTER}
                        setSnackbarData={setSnackbarData}
                        currentZoomLevel={currentZoomLevel}
                        hasSuccess={false}
                        hasError={false}
                        selectedPostValuePath={selectedHistoryPostValuePath}
                        unusedZoomLevelCount={0}
                        zoomLevelIcons={new Map([
                            [ZOOM_LEVEL.one, ''],
                            [ZOOM_LEVEL.two, ''],
                            [ZOOM_LEVEL.three, ''],
                            [ZOOM_LEVEL.four, ''],
                            [ZOOM_LEVEL.five, ''],
                        ])}
                        resizingAnnotationView={false}
                    />
                </ErrorBoundary>
            )}
        </HistoryPreviewContainer>
    ) : null;

    const PublishingActionsContent = isPublishingMode ? (
        <HistoryScopeRow>
            <HistoryScopeLabel>Publishing</HistoryScopeLabel>
            <HistoryActionRow>
                {showPreviewControls && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={definePreviewMode
                            ? themeObj.verascopeColor.purple300
                            : themeObj.color.cream400}
                        icon={SplitPageIcon}
                        text={definePreviewMode ? 'Click a paragraph…' : 'Set Cutoff'}
                        loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                        disabled={
                            !selectedCheckpointValue
                            || loadingSelectedCheckpoint
                            || savingPreviewCutoff
                            || publishingMode != null
                            || !currentScopeSupportsPreviewCutoff
                        }
                        tooltip={{
                            active: true,
                            text: setCutoffDisabledTooltip || VERSION_HISTORY_SET_CUTOFF_TOOLTIP,
                            side: TOOLTIP_TYPE.top,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => setDefinePreviewMode((currentValue) => !currentValue),
                            onMouseDown: () => setDefinePreviewMode((currentValue) => !currentValue),
                        })}
                    />
                )}
                {showPreviewControls && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.purple400}
                        icon={SplitPageIcon}
                        text="Publish Cutoff"
                        loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                        disabled={!selectedCheckpointHasPreviewCutoff || loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null || !currentScopeSupportsPreviewCutoff || previewCutoffOnlyRequiresProdTarget || previewCutoffOnlyWouldBeNoOp}
                        tooltip={{
                            active: !!previewCutoffOnlyDisabledTooltip,
                            text: previewCutoffOnlyDisabledTooltip,
                            side: TOOLTIP_TYPE.top,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: handlePublishPreviewCutoffOnly,
                            onMouseDown: handlePublishPreviewCutoffOnly,
                        })}
                    />
                )}
                {showPreviewControls && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.purple400}
                        icon={PaperplaneIcon}
                        text="Publish Preview"
                        loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                        disabled={!selectedCheckpointHasPreviewCutoff || loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null || !currentScopeSupportsPreviewCutoff || previewPublishRequiresProdTarget || previewPublishWouldBeNoOp}
                        tooltip={{
                            active: !!previewPublishDisabledTooltip,
                            text: previewPublishDisabledTooltip,
                            side: TOOLTIP_TYPE.top,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => handleOpenPublishModal(POST_COLLAB_PUBLISH_MODE.preview),
                            onMouseDown: () => handleOpenPublishModal(POST_COLLAB_PUBLISH_MODE.preview),
                        })}
                    />
                )}
                {test && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.orange200}
                        icon={PaperplaneIcon}
                        text="Publish Full"
                        loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                        disabled={!selectedCheckpoint || loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null || fullPublishRequiresPreviewCutoff || fullPublishWouldBeNoOp}
                        tooltip={{
                            active: !!fullPublishDisabledTooltip,
                            text: fullPublishDisabledTooltip,
                            side: TOOLTIP_TYPE.top,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => handleOpenPublishModal(POST_COLLAB_PUBLISH_MODE.full),
                            onMouseDown: () => handleOpenPublishModal(POST_COLLAB_PUBLISH_MODE.full),
                        })}
                    />
                )}
                <Button
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    type={BUTTON_TYPE.solid}
                    background={themeObj.color.cream400}
                    icon={DownloadIcon}
                    text="Export"
                    loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                    disabled={!selectedCheckpointValue || loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                    tooltip={{
                        active: true,
                        text: VERSION_HISTORY_EXPORT_JSON_TOOLTIP,
                        side: TOOLTIP_TYPE.top,
                    }}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onCursorEnter(
                            CURSOR_TARGET.editorButton,
                            ['click'],
                            e.currentTarget as HTMLElement,
                        ),
                        onMouseLeave: onCursorLeave,
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: exportSelectedVersionJson,
                        onMouseDown: exportSelectedVersionJson,
                    })}
                />
                {!selectedCheckpointIsLatest && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.orange200}
                        text="Restore"
                        icon={ClockIcon}
                        loading={loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null}
                        disabled={!selectedCheckpointValue || loadingSelectedCheckpoint || savingPreviewCutoff || publishingMode != null || restoreScopeMismatch}
                        tooltip={{
                            active: !!restoreDisabledTooltip,
                            text: restoreDisabledTooltip,
                            side: TOOLTIP_TYPE.top,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => setRestoreModalVisible(true),
                            onMouseDown: () => setRestoreModalVisible(true),
                        })}
                    />
                )}
            </HistoryActionRow>
        </HistoryScopeRow>
    ) : null;

    const PublishSummaryCard = annotationRepairSummary != null && !annotationRepairSummaryDismissed ? (
        <HistoryPublishSummaryCard>
            <HistoryPublishSummaryTitle>
                Publish result
            </HistoryPublishSummaryTitle>
            <HistoryPublishSummarySubtitle>
                Full version published to production.
            </HistoryPublishSummarySubtitle>
            <HistoryPublishSummaryCounts>
                <HistoryPublishSummaryCountRow>
                    <HistoryPublishSummaryCountLabel>
                        Reattached
                    </HistoryPublishSummaryCountLabel>
                    <HistoryPublishSummaryCountValue>
                        {annotationRepairSummary.reattached}
                    </HistoryPublishSummaryCountValue>
                </HistoryPublishSummaryCountRow>
                <HistoryPublishSummaryCountRow>
                    <HistoryPublishSummaryCountLabel>
                        Detached
                    </HistoryPublishSummaryCountLabel>
                    <HistoryPublishSummaryCountValue>
                        {annotationRepairSummary.detached}
                    </HistoryPublishSummaryCountValue>
                </HistoryPublishSummaryCountRow>
                <HistoryPublishSummaryCountRow>
                    <HistoryPublishSummaryCountLabel>
                        Unchanged
                    </HistoryPublishSummaryCountLabel>
                    <HistoryPublishSummaryCountValue>
                        {annotationRepairSummary.unchanged}
                    </HistoryPublishSummaryCountValue>
                </HistoryPublishSummaryCountRow>
                <HistoryPublishSummaryCountRow>
                    <HistoryPublishSummaryCountLabel>
                        Failed
                    </HistoryPublishSummaryCountLabel>
                    <HistoryPublishSummaryCountValue>
                        {annotationRepairSummary.failed}
                    </HistoryPublishSummaryCountValue>
                </HistoryPublishSummaryCountRow>
            </HistoryPublishSummaryCounts>
            {annotationRepairSummary.detached > 0 && (
                <HistoryPublishSummaryNote>
                    Detached annotations are hidden from readers until repaired.
                </HistoryPublishSummaryNote>
            )}
            {annotationRepairSummary.failed > 0 && annotationRepairSummary.failedItems != null && (
                <>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.color.neutral300}
                        text={annotationRepairFailedDetailsVisible ? 'Hide failed details' : 'View failed details'}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => setAnnotationRepairFailedDetailsVisible((currentValue) => !currentValue),
                            onMouseDown: () => setAnnotationRepairFailedDetailsVisible((currentValue) => !currentValue),
                        })}
                    />
                    {annotationRepairFailedDetailsVisible && (
                        <HistoryPublishSummaryFailedList>
                            {annotationRepairSummary.failedItems.map((item) => (
                                <HistoryPublishSummaryFailedItem key={`${item.annotationId}-${item.reason}`}>
                                    {`${item.annotationId}: ${item.reason}`}
                                </HistoryPublishSummaryFailedItem>
                            ))}
                        </HistoryPublishSummaryFailedList>
                    )}
                </>
            )}
            <HistoryPublishSummaryActions>
                {annotationRepairSummary.detached > 0 && detachedAnnotationsRoute && userIsAdmin && (
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.orange200}
                        text="View Detached Annotations"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.editorButton,
                                ['click'],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: onCursorLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: handleOpenDetachedAnnotationsRoute,
                            onMouseDown: handleOpenDetachedAnnotationsRoute,
                        })}
                    />
                )}
                <Button
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    type={BUTTON_TYPE.solid}
                    background={themeObj.color.neutral300}
                    text="Dismiss"
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onCursorEnter(
                            CURSOR_TARGET.editorButton,
                            ['click'],
                            e.currentTarget as HTMLElement,
                        ),
                        onMouseLeave: onCursorLeave,
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: () => setAnnotationRepairSummaryDismissed(true),
                        onMouseDown: () => setAnnotationRepairSummaryDismissed(true),
                    })}
                />
            </HistoryPublishSummaryActions>
        </HistoryPublishSummaryCard>
    ) : null;

    const RightPaneContent = isPublishingMode ? (
        <HistoryPreviewPane>
            {SelectedVersionPreviewContent}
            <HistoryPreviewFooter>
                {PublishingActionsContent}
                {PublishSummaryCard}
            </HistoryPreviewFooter>
        </HistoryPreviewPane>
    ) : (
        <HistoryAccessPane>
            {AccessControlsContent}
        </HistoryAccessPane>
    );

    const renderRestoreModal = (): React.ReactElement => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={restoreModalVisible}
            closeModal={() => setRestoreModalVisible(false)}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={restoreModalMessage}
                callback={handleConfirmRestore}
                handleClose={() => setRestoreModalVisible(false)}
                acceptText="Restore"
                hideCancel={false}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );

    const renderPublishModal = (): React.ReactElement => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={publishModalMode != null}
            closeModal={() => setPublishModalMode(null)}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={publishModalMessage}
                callback={handleConfirmPublish}
                handleClose={() => setPublishModalMode(null)}
                acceptText={publishModalMode === POST_COLLAB_PUBLISH_MODE.preview ? 'Publish preview' : 'Publish full'}
                hideCancel={false}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );

    if (!visible || !post || !selectedDialogDocId || !selectedHistoryDocId) return null;

    return (
        <>
            <Modal
                hasContainer={false}
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={visible}
                closeModal={onClose}
            >
                <HistoryModalContainer $compact={isAccessMode}>
                    <HistoryModalHeader>
                        <HistoryModalCloseButtonContainer>
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.floating}
                                background={themeObj.verascopeColor.purple300}
                                height={2.5 * BODY_FONT_SIZE}
                                width={2.5 * BODY_FONT_SIZE}
                                icon={CrossIcon}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: (e) => onCursorEnter(
                                        CURSOR_TARGET.modalCloseButton,
                                        ['click'],
                                        e.currentTarget as HTMLElement,
                                    ),
                                    onMouseLeave: onCursorLeave,
                                })}
                                {...getPressEventHandlers({ onTouchStart: onClose, onMouseDown: onClose })}
                            />
                        </HistoryModalCloseButtonContainer>
                        <HistoryModalTitle>
                            {isPublishingMode ? 'Publishing' : 'Access'}
                        </HistoryModalTitle>
                        <HistoryModalSubtitle>
                            {isPublishingMode
                                ? 'Browse checkpoint history on the left, preview the selected version on the right, and publish from the current checkpoint.'
                                : 'Choose the scope whose access settings you want to edit, then save or publish those settings to production.'}
                        </HistoryModalSubtitle>
                    </HistoryModalHeader>
                    {isPublishingMode ? (
                        <HistoryModalBody>
                            {HistorySidebarContent}
                            {RightPaneContent}
                        </HistoryModalBody>
                    ) : (
                        <HistorySinglePaneBody>
                            {RightPaneContent}
                        </HistorySinglePaneBody>
                    )}
                </HistoryModalContainer>
            </Modal>
            {renderRestoreModal()}
            {renderPublishModal()}
        </>
    );
}

export default PostPublishingDialog;
